/*
 * https://github.com/5orenso
 *
 * Copyright (c) 2020 Øistein Sørensen
 * Licensed under the MIT license.
 */

import util from 'preact-util';
import md5 from 'crypto-js/md5';

import countries from 'i18n-iso-countries';

// Load the languages you need
import enLocale from 'i18n-iso-countries/langs/en.json';
import noLocale from 'i18n-iso-countries/langs/no.json';
import frLocale from 'i18n-iso-countries/langs/fr.json';
import deLocale from 'i18n-iso-countries/langs/de.json';

countries.registerLocale(enLocale);
countries.registerLocale(noLocale);
countries.registerLocale(frLocale);
countries.registerLocale(deLocale);

const langs = ['en', 'no', 'fr', 'de'];

const reMarks = /[\u0300-\u036f]/g;
const reSpace = /[\s\-_]+/g;

const ukSubdivisions = {
    'scotland': 'GB-SCT',
    'england': 'GB-ENG',
    'wales': 'GB-WLS',
    'cymru': 'GB-WLS',
    'northern ireland': 'GB-NIR',
    'ulster': 'GB-NIR',
    'ecosse': 'GB-SCT',
    'schottland': 'GB-SCT',
    'angleterre': 'GB-ENG',
    'pays de galles': 'GB-WLS',
    'irlande du nord': 'GB-NIR',
    'nordirland': 'GB-NIR'
};

const aliasToAlpha2 = {
    'czech': 'CZ',
    'czech republic': 'CZ',
    'ivory coast': 'CI',
    'russia': 'RU',
    'uk': 'GB'
};

// Precomputed indices
let alpha2ToNames = new Map();         // alpha2 -> Set(normalized name)
let nameToAlpha2 = new Map();          // normalized name -> alpha2 (best guess)
let prefixIndex = new Map();           // 3-letter prefix -> Set(alpha2)

// Build once at startup
(function buildCountryIndexes() {
    alpha2ToNames = new Map();
    for (const lang of langs) {
        const names = countries.getNames(lang);
        for (const [alpha2, name] of Object.entries(names)) {
            const n = normalizeName(name);
            if (!alpha2ToNames.has(alpha2)) alpha2ToNames.set(alpha2, new Set());
            alpha2ToNames.get(alpha2).add(n);
        }
    }
    // exact-name map (first writer wins; keeps things simple)
    nameToAlpha2 = new Map();
    for (const [alpha2, set] of alpha2ToNames.entries()) {
        for (const n of set) {
            if (!nameToAlpha2.has(n)) nameToAlpha2.set(n, alpha2);
            // build a 3-char prefix index
            const p = n.slice(0, 3);
            if (p.length === 3) {
                if (!prefixIndex.has(p)) prefixIndex.set(p, new Set());
                prefixIndex.get(p).add(alpha2);
            }
        }
    }
})();

// local helper (same behavior, hoisted regex)
function normalizeName(s) {
    return String(s || '')
        .toLowerCase()
        .normalize('NFD').replace(reMarks, '')
        .replace(reSpace, ' ')
        .trim();
}

// const MARGIN_TOP_W_SUBMENU = '71px'; // -19
// const MARGIN_TOP_W_SUBMENU_TABS = '120px';
// const MARGIN_TOP = '41px';
// const SUB_MENU_MARGIN_TOP = '40px'; // -16
// // const MARGIN_TOP_BACK = '34px';
// // const MARGIN_TOP_BACK_W_SUBMENU = '70px';
// // const MARGIN_TOP_BACK_W_SUBMENU_TABS = '100px';
// const MARGIN_TOP_BACK = '2px';
// const MARGIN_TOP_BACK_W_SUBMENU = '2px';
// const MARGIN_TOP_BACK_W_SUBMENU_TABS = '2px';

const APP_MARGIN_TOP_W_SUBMENU = '108px'; // -19
const APP_MARGIN_TOP_W_SUBMENU_TABS = '157px';
const APP_MARGIN_TOP = '57px';
const APP_SUB_MENU_MARGIN_TOP = '56px'; // -16
// const APP_MARGIN_TOP_BACK = '61px';
// const APP_MARGIN_TOP_BACK_W_SUBMENU = '97px';
// const APP_MARGIN_TOP_BACK_W_SUBMENU_TABS = '127px';
const APP_MARGIN_TOP_BACK = '45px';
const APP_MARGIN_TOP_BACK_W_SUBMENU = '45px';
const APP_MARGIN_TOP_BACK_W_SUBMENU_TABS = '45px';

const MARGIN_BOTTOM = '200px';

const MAX_LOADINDEX = 200;
const NORMAL_SPEED_KMT = 13;
const ELEVATION_THRESHOLD = 3;
const WINDOW_SIZE_ARRAY_SMOOTH = 20;

const RED_DAYS = [0, 6];
const DAYS = {
    no: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    es: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    de: ['Sun', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
};
const DAYS_SHORT = {
    no: ['S', 'M', 'T', 'O', 'T', 'F', 'L'],
    en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    es: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
    de: ['S', 'M', 'D', 'M', 'D', 'F', 'S'],
};
const DAYS_FULL = {
    no: ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    es: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
    de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
};
const MONTHS = {
    no: ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'],
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    es: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    de: ['Jän', 'Feb', 'Mär', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
};

const RANKING_COLORS = [
    '#FA0007',
    '#325E75',
    '#990012',
    '#694C11',
    '#A800A7',
    '#0A007E',
    '#1B569D',
    '#51B73C',
    '#C47F22',
    '#139BFF',

    '#FA0007',
    '#325E75',
    '#990012',
    '#694C11',
    '#A800A7',
    '#0A007E',
    '#1B569D',
    '#51B73C',
    '#C47F22',
    '#139BFF',

    '#FA0007',
    '#325E75',
    '#990012',
    '#694C11',
    '#A800A7',
    '#0A007E',
    '#1B569D',
    '#51B73C',
    '#C47F22',
    '#139BFF',

    '#FA0007',
    '#325E75',
    '#990012',
    '#694C11',
    '#A800A7',
    '#0A007E',
    '#1B569D',
    '#51B73C',
    '#C47F22',
    '#139BFF',

    '#FA0007',
    '#325E75',
    '#990012',
    '#694C11',
    '#A800A7',
    '#0A007E',
    '#1B569D',
    '#51B73C',
    '#C47F22',
    '#139BFF',

    '#FA0007',
    '#325E75',
    '#990012',
    '#694C11',
    '#A800A7',
    '#0A007E',
    '#1B569D',
    '#51B73C',
    '#C47F22',
    '#139BFF',

    '#FA0007',
    '#325E75',
    '#990012',
    '#694C11',
    '#A800A7',
    '#0A007E',
    '#1B569D',
    '#51B73C',
    '#C47F22',
    '#139BFF',
];

const COLOR_NAMES = {
    navy: '#001f3f',
    aqua: '#7FDBFF',
    teal: '#39CCCC',
    olive: '#3D9970',
    green: '#2ECC40',
    lime: '#01FF70',
    yellow: '#FFDC00',
    orange: '#FF851B',
    red: '#FF4136',
    maroon: '#85144b',
    fuchsia: '#F012BE',
    purple: '#B10DC9',
    gray: '#AAAAAA',
    silver: '#DDDDDD',

    alertRed: '#FF0000ff',
    alertScarlet: '#FF2000ff',
    alertRedOrange: '#FF4000ff',
    alertSafetyOrange: '#FF6000ff',
    alertOrange: '#FF8000ff',
    alertOrangePeel: '#FF9F00ff',
    alertAmber: '#FFBF00ff',
    alertGoldenYellow: '#FFDF00ff',
    alertLemonGlacier: '#FFFF00ff',

    vermilion: '#E60000ff',
    vermilion2: '#E61D00ff',
    coquelicot: '#E63A00ff',
    persimmon: '#E65600ff',
    princetonOrange: '#E67300ff',
    harvestGold: '#E59000ff',
    goldenrod: '#E5AD00ff',
    safetyYellow: '#E5C900ff',
    xanthic: '#E5E600ff',

    blue: '#0000E6ff',
    blue2: '#1C0FE5ff',
    blue3: '#391FE4ff',
    hanPurple: '#552EE3ff',
    blueViolet: '#713DE2ff',
    purpleX11: '#8E4DE2ff',
    mediumOrchid: '#AA5CE1ff',
    frenchMauve: '#C66BE0ff',
    orchid: '#E37BDFff',
    persianPink: '#FF8ADEff',

    neonGreen: '#00E600ff',
    limeGreen: '#00CD17ff',
    yellowGreen: '#00B42Eff',
    greenPigment: '#009C46ff',
    spanishViridian: '#00835Dff',
    ming: '#006A74ff',
    usafaBlue: '#00518Bff',
    egyptianBlue: '#0039A3ff',
    bluePantone: '#0020BAff',
    mediumBlue: '#0007D1ff',
};

const HISTORY_TYPE_COLOR_MAP = {
    other: 'limeGreen',
    update: 'yellowGreen',
    newStraw: 'greenPigment',
    clawClip: 'spanishViridian',
    bodyScore: 'ming',
    weight: 'usafaBlue',

    maturity: 'blue',
    mating: 'blue3',
    vaccine: 'blueViolet',
    deworming: 'mediumOrchid',
    veterinarianVisit: 'persianPink',

    fight: 'vermilion',
    injury: 'coquelicot',
    sick: 'persimmon',
    unwantedMating: 'princetonOrange',
    stoolProblem: 'harvestGold',
    diarrhea: 'goldenrod',
    foodLeftover: 'xanthic',

    massage: 'egyptianBlue',
    animalchiropractor: 'bluePantone',
};

const HISTORY_PRIORITY_LIST_1 = [
    'fight',
    'injury',
    'sick',
    'unwantedMating',
    'stoolProblem',
    'diarrhea',
    'foodLeftover',
];

const HISTORY_PRIORITY_LIST_2 = [
    'maturity',
    'mating',
    'vaccine',
    'deworming',
    'veterinarianVisit',
];

const HISTORY_PRIORITY_LIST_3 = [
    'other',
    'update',
    'newStraw',
    'clawClip',
    'bodyScore',
    'weight',
    'massage',
    'animalchiropractor',
];

const HISTORY_TYPE_MAP = {
    no: {
        other: 'Annet',
        maturity: 'Løpetid',
        update: 'Oppdatering',
        injury: 'Skade',
        fight: 'Slåsskamp',
        sick: 'Syk',
        vaccine: 'Vaksine',
        weight: 'Veiing',
        veterinarianVisit: 'Vetrinærbesøk',
        newStraw: 'Ny halm/treull',
        clawClip: 'Kloklipp',
        mating: 'Parring',
        unwantedMating: 'Tjuvparring',
        diarrhea: 'Diarè',
        stoolProblem: 'Avføringsproblem',
        deworming: 'Ormekur',
        foodLeftover: 'Ikke spist opp maten',
        bodyScore: 'Bodyscore',
        massage: 'Massasje',
        animalchiropractor: 'Dyrekiropraktor',
    },
    en: {
        other: 'Other',
        maturity: 'On heat',
        update: 'Update',
        injury: 'Injury',
        fight: 'Fight',
        sick: 'Sick',
        vaccine: 'Vaccine',
        weight: 'Weight',
        veterinarianVisit: 'Veterinarian Visit',
        newStraw: 'New Straw',
        clawClip: 'Claw Clip',
        mating: 'Mating',
        unwantedMating: 'Unwanted Mating',
        diarrhea: 'Diarrhea',
        stoolProblem: 'Stool Problem',
        deworming: 'Deworming',
        foodLeftover: 'Food Leftover',
        bodyScore: 'BodyScore',
        massage: 'Massage',
        animalchiropractor: 'Animal chiropractor',
    },
    es: {
        other: 'Otro',
        maturity: 'En celo',
        update: 'Actualizaciones',
        injury: 'Lesión',
        fight: 'Pelea',
        sick: 'Enfermo',
        vaccine: 'Vacunas',
        weight: 'Control del peso',
        veterinarianVisit: 'Visita veterinaria',
        newStraw: 'Paja nueva',
        clawClip: 'Cortar las uñas',
        mating: 'Cruzamiento',
        unwantedMating: 'Cruza no deseada',
        diarrhea: 'Diarrea',
        stoolProblem: 'Problemas con las heces',
        deworming: 'Desparasitación',
        foodLeftover: 'No terminó de comer',
        bodyScore: 'Bodyscore',
        massage: 'Masaje',
        animalchiropractor: 'Quiropráctico',
    },
    de: {
        other: 'Andere',
        maturity: 'Läufigkeit',
        update: 'Aktualisierung',
        injury: 'Verletzung',
        fight: 'Kampf',
        sick: 'Erkrankung',
        vaccine: 'Impfung',
        weight: 'Wiegen',
        veterinarianVisit: 'Tierarztbesuch',
        newStraw: 'Frisches Stroh',
        clawClip: 'Klauen geschnitten',
        mating: 'Parung',
        unwantedMating: 'Ungewollte Parung',
        diarrhea: 'Durchfall',
        stoolProblem: 'Stuhlproblem',
        deworming: 'Entwurmung',
        foodLeftover: 'Hat nicht alles aufgefressen',
        bodyScore: 'BodyScore',
        massage: 'Massage',
        animalchiropractor: 'Tier Chiropraktiker',
    },
};

const VACCINE_TYPES = {
    rabies: {
        name: 'Rabies (3 år)',
        duration: 365 * 3,
    },
    deworming: {
        name: 'Ormekur - reise',
        duration: 28,
    },
    BbPi: {
        name: 'BbPi - Nesevaksine (1 år)',
        duration: 365 * 1,
    },
    DHP: {
        name: 'DHP (3 år)',
        duration: 365 * 3,
    },
    DHPPi: {
        name: 'DHPPi (3 år)',
        duration: 365 * 3,
    },
};

const VACCINE_AGENS_VETERINARY = [
    {
        id: 100,
        name: 'Distemper',
        agens: [
            {
                name: 'Distemper',
                lang: {
                    no: 'Valpesyke',
                    en: 'Distemper',
                },
                durationMonths: 36,
                karensDays: 14,
            },
        ],
    },
    {
        id: 101,
        name: 'Hepatitt',
        agens: [
            {
                name: 'Hepatitt',
                lang: {
                    no: 'Smittsom leverbetennelse CAV1/CAV2',
                    en: 'Hepatitt',
                },
                durationMonths: 36,
                karensDays: 14,
            },
        ],
    },
    {
        id: 102,
        name: 'Parvo CPV',
        agens: [
            {
                name: 'Parvo CPV',
                lang: {
                    no: 'Parvo CPV',
                    en: 'Parvo CPV',
                },
                durationMonths: 36,
                karensDays: 14,
            },
        ],
    },
    {
        id: 103,
        name: 'Parainfluensa CPiV',
        agens: [
            {
                name: 'Parainfluensa CPiV',
                lang: {
                    no: 'Parainfluensa CPiV',
                    en: 'Parainfluensa CPiV',
                },
                durationMonths: 12,
                karensDays: 14,
            },
        ],
    },
    {
        id: 104,
        name: 'Bordetella Bronchoseptica',
        agens: [
            {
                name: 'Bordetella Bronchoseptica',
                lang: {
                    no: 'Bordetella Bronchoseptica',
                    en: 'Bordetella Bronchoseptica',
                },
                durationMonths: 12,
                karensDays: 21,
            },
        ],
    },
    {
        id: 105,
        name: 'Rabies',
        agens: [
            {
                name: 'Rabies',
                lang: {
                    no: 'Rabies',
                    en: 'Rabies',
                },
                durationMonths: 36,
                karensDays: 14,
            },
        ],
    },
];

const VACCINE_TYPES_VETERINARY = [
    {
        id: 1,
        name: 'Nobivac BbPi nasal',
        agens: [
            {
                name: 'Parainfluensa',
                durationMonths: 12,
                karensDays: 42,
            },
            {
                name: 'Bordetella',
                durationMonths: 12,
                karensDays: 42,
            },
            {
                name: 'Bronchioseptica',
                durationMonths: 12,
                karensDays: 42,
            },
        ],
        image: '/images/nobivac-BbPi.jpg',
    },
    {
        id: 3,
        name: 'Nobivac DHP',
        agens: [
            {
                name: 'Valpesyke',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Hepatitt',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Parvovirus',
                durationMonths: 36,
                karensDays: 14,
            },
        ],
        image: '/images/nobivac-DHP.jpg',
    },
    {
        id: 4,
        name: 'Nobivac DHPPi',
        agens: [
            {
                name: 'Valpesyke',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Hepatitt',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Parvovirus',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Parainfluensa',
                durationMonths: 12,
                karensDays: 14,
            },
        ],
        comment: '3 år valpesyke, hepatitt og parvovirus, 12 mnd parainfluensa',
        image: '/images/nobivac-DHPPi.jpg',
    },
    {
        id: 5,
        name: 'Nobivac Pi',
        agens: [
            {
                name: 'Parainfluensa',
                durationMonths: 12,
                karensDays: 14,
            },
        ],
        image: '/images/nobivac-Pi.jpg',
    },
    {
        id: 2,
        name: 'Versican Plus Bb oral',
        agens: [
            {
                name: 'Bordetella',
                durationMonths: 12,
                karensDays: 42,
            },
            {
                name: 'Bronchioseptica',
                durationMonths: 12,
                karensDays: 42,
            },
        ],
        image: '/images/versican-plus-Bb-oral.jpg',
    },
    {
        id: 6,
        name: 'Versican Plus DHPPi',
        agens: [
            {
                name: 'Valpesyke',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Hepatitt',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Parvovirus',
                durationMonths: 36,
                karensDays: 14,
            },
            {
                name: 'Parainfluensa',
                durationMonths: 12,
                karensDays: 14,
            },
        ],
        comment: '3 år valpesyke, hepatitt og parvovirus 12 mnd parainfluensa',
        image: '/images/versican-plus-DHPPi.jpg',
    },
    {
        id: 7,
        name: 'Versican Plus Pi',
        agens: [
            {
                name: 'Parainfluensa',
                durationMonths: 12,
                karensDays: 14,
            },
        ],
        image: '/images/versican-plus-Pi.jpg',
    },
];

const TROPHY_IMAGES = {
    founder: './assets/trophys/signup-founder.png',
    veryEarlyUser: './assets/trophys/signup-very-early.png',
    earlyUser: './assets/trophys/signup-early.png',
    '2020-2021-100km': './assets/trophys/2020-2021-100km.png',
    '2020-2021-250km': './assets/trophys/2020-2021-250km.png',
    '2020-2021-500km': './assets/trophys/2020-2021-500km.png',
    '2020-2021-1000km': './assets/trophys/2020-2021-1000km.png',
    '2020-2021-1500km': './assets/trophys/2020-2021-1500km.png',
    '2020-2021-2000km': './assets/trophys/2020-2021-2000km.png',
    '2020-2021-2500km': './assets/trophys/2020-2021-2500km.png',
    '2020-2021-3000km': './assets/trophys/2020-2021-3000km.png',
    '2020-2021-3500km': './assets/trophys/2020-2021-3500km.png',
    '2020-2021-4000km': './assets/trophys/2020-2021-4000km.png',

    '2020-2021-everest-x1': './assets/trophys/2020-2021-everest-x1.png',
    '2020-2021-everest-x5': './assets/trophys/2020-2021-everest-x5.png',
    '2020-2021-everest-x10': './assets/trophys/2020-2021-everest-x10.png',

    '2021-2022-100km': './assets/trophys/2021-2022-100km.png',
    '2021-2022-250km': './assets/trophys/2021-2022-250km.png',
    '2021-2022-500km': './assets/trophys/2021-2022-500km.png',
    '2021-2022-1000km': './assets/trophys/2021-2022-1000km.png',
    '2021-2022-1500km': './assets/trophys/2021-2022-1500km.png',
    '2021-2022-2000km': './assets/trophys/2021-2022-2000km.png',
    '2021-2022-2500km': './assets/trophys/2021-2022-2500km.png',
    '2021-2022-3000km': './assets/trophys/2021-2022-3000km.png',
    '2021-2022-3500km': './assets/trophys/2021-2022-3500km.png',
    '2021-2022-4000km': './assets/trophys/2021-2022-4000km.png',

    '2021-2022-everest-x1': './assets/trophys/2021-2022-everest-x1.png',
    '2021-2022-everest-x5': './assets/trophys/2021-2022-everest-x5.png',
    '2021-2022-everest-x10': './assets/trophys/2021-2022-everest-x10.png',

    '2022-2023-10km': './assets/trophys/2022-2023-10km.png',
    '2022-2023-100km': './assets/trophys/2022-2023-100km.png',
    '2022-2023-250km': './assets/trophys/2022-2023-250km.png',
    '2022-2023-500km': './assets/trophys/2022-2023-500km.png',
    '2022-2023-1000km': './assets/trophys/2022-2023-1000km.png',
    '2022-2023-1500km': './assets/trophys/2022-2023-1500km.png',
    '2022-2023-2000km': './assets/trophys/2022-2023-2000km.png',
    '2022-2023-2500km': './assets/trophys/2022-2023-2500km.png',
    '2022-2023-3000km': './assets/trophys/2022-2023-3000km.png',
    '2022-2023-3500km': './assets/trophys/2022-2023-3500km.png',
    '2022-2023-4000km': './assets/trophys/2022-2023-4000km.png',

    '2022-2023-everest-x1': './assets/trophys/2022-2023-everest-x1.png',
    '2022-2023-everest-x5': './assets/trophys/2022-2023-everest-x5.png',
    '2022-2023-everest-x10': './assets/trophys/2022-2023-everest-x10.png',
};

const COUNTRIES = [
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'Afghanistan',
      capital: 'Kabul',
      fips: 'AF',
      iso2: 'AF',
      iso3: 'AFG',
      isoNo: '004',
      internet: 'AF'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Albania',
      capital: 'Tirana',
      fips: 'AL',
      iso2: 'AL',
      iso3: 'ALB',
      isoNo: '008',
      internet: 'AL'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'Algeria',
      capital: 'Algiers',
      fips: 'AG',
      iso2: 'DZ',
      iso3: 'DZA',
      isoNo: '012',
      internet: 'DZ'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'American Samoa',
      capital: 'Pago Pago',
      fips: 'AQ',
      iso2: 'AS',
      iso3: 'ASM',
      isoNo: '016',
      internet: 'AS'
    },
    {
      continent: 'Europe',
      region: 'South West Europe',
      country: 'Andorra',
      capital: 'Andorra la Vella',
      fips: 'AN',
      iso2: 'AD',
      iso3: 'AND',
      isoNo: '020',
      internet: 'AD'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Angola',
      capital: 'Luanda',
      fips: 'AO',
      iso2: 'AO',
      iso3: 'AGO',
      isoNo: '024',
      internet: 'AO'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Anguilla',
      capital: 'The Valley',
      fips: 'AV',
      iso2: 'AI',
      iso3: 'AIA',
      isoNo: '660',
      internet: 'AI'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Antigua and Barbuda',
      capital: "Saint John's",
      fips: 'AC',
      iso2: 'AG',
      iso3: 'ATG',
      isoNo: '028',
      internet: 'AG'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Argentina',
      capital: 'Buenos Aires',
      fips: 'AR',
      iso2: 'AR',
      iso3: 'ARG',
      isoNo: '032',
      internet: 'AR'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Armenia',
      capital: 'Yerevan',
      fips: 'AM',
      iso2: 'AM',
      iso3: 'ARM',
      isoNo: '051',
      internet: 'AM'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Aruba',
      capital: 'Oranjestad',
      fips: 'AA',
      iso2: 'AW',
      iso3: 'ABW',
      isoNo: '533',
      internet: 'AW'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Australia',
      capital: 'Canberra',
      fips: 'AS',
      iso2: 'AU',
      iso3: 'AUS',
      isoNo: '036',
      internet: 'AU'
    },
    {
      continent: 'Europe',
      region: 'Central Europe',
      country: 'Austria',
      capital: 'Vienna',
      fips: 'AU',
      iso2: 'AT',
      iso3: 'AUT',
      isoNo: '040',
      internet: 'AT'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Azerbaijan',
      capital: 'Baku (Baki)',
      fips: 'AJ',
      iso2: 'AZ',
      iso3: 'AZE',
      isoNo: '031',
      internet: 'AZ'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'The Bahamas',
      capital: 'Nassau',
      fips: 'BF',
      iso2: 'BS',
      iso3: 'BHS',
      isoNo: '044',
      internet: 'BS'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Bahrain',
      capital: 'Manama',
      fips: 'BA',
      iso2: 'BH',
      iso3: 'BHR',
      isoNo: '048',
      internet: 'BH'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'Bangladesh',
      capital: 'Dhaka',
      fips: 'BG',
      iso2: 'BD',
      iso3: 'BGD',
      isoNo: '050',
      internet: 'BD'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Barbados',
      capital: 'Bridgetown',
      fips: 'BB',
      iso2: 'BB',
      iso3: 'BRB',
      isoNo: '052',
      internet: 'BB'
    },
    {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: 'Belarus',
      capital: 'Minsk',
      fips: 'BO',
      iso2: 'BY',
      iso3: 'BLR',
      isoNo: '112',
      internet: 'BY'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Belgium',
      capital: 'Brussels',
      fips: 'BE',
      iso2: 'BE',
      iso3: 'BEL',
      isoNo: '056',
      internet: 'BE'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'Belize',
      capital: 'Belmopan',
      fips: 'BH',
      iso2: 'BZ',
      iso3: 'BLZ',
      isoNo: '084',
      internet: 'BZ'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Benin',
      capital: 'Porto-Novo',
      fips: 'BN',
      iso2: 'BJ',
      iso3: 'BEN',
      isoNo: '204',
      internet: 'BJ'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Bermuda',
      capital: 'Hamilton',
      fips: 'BD',
      iso2: 'BM',
      iso3: 'BMU',
      isoNo: '060',
      internet: 'BM'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'Bhutan',
      capital: 'Thimphu',
      fips: 'BT',
      iso2: 'BT',
      iso3: 'BTN',
      isoNo: '064',
      internet: 'BT'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Bolivia',
      capital: 'La Paz / Sucre',
      fips: 'BL',
      iso2: 'BO',
      iso3: 'BOL',
      isoNo: '068',
      internet: 'BO'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Bosnia and Herzegovina',
      capital: 'Sarajevo',
      fips: 'BK',
      iso2: 'BA',
      iso3: 'BIH',
      isoNo: '070',
      internet: 'BA'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Botswana',
      capital: 'Gaborone',
      fips: 'BC',
      iso2: 'BW',
      iso3: 'BWA',
      isoNo: '072',
      internet: 'BW'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Brazil',
      capital: 'Brasilia',
      fips: 'BR',
      iso2: 'BR',
      iso3: 'BRA',
      isoNo: '076',
      internet: 'BR'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'British Virgin Islands',
      capital: 'Road Town',
      fips: 'VI',
      iso2: 'VG',
      iso3: 'VGB',
      isoNo: '092',
      internet: 'VG'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Brunei',
      capital: 'Bandar Seri Begawan',
      fips: 'BX',
      iso2: 'BN',
      iso3: 'BRN',
      isoNo: '096',
      internet: 'BN'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Bulgaria',
      capital: 'Sofia',
      fips: 'BU',
      iso2: 'BG',
      iso3: 'BGR',
      isoNo: '100',
      internet: 'BG'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Burkina Faso',
      capital: 'Ouagadougou',
      fips: 'UV',
      iso2: 'BF',
      iso3: 'BFA',
      isoNo: '854',
      internet: 'BF'
    },
    {
      continent: 'Africa',
      region: 'Central Africa',
      country: 'Burundi',
      capital: 'Bujumbura',
      fips: 'BY',
      iso2: 'BI',
      iso3: 'BDI',
      isoNo: '108',
      internet: 'BI'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Cambodia',
      capital: 'Phnom Penh',
      fips: 'CB',
      iso2: 'KH',
      iso3: 'KHM',
      isoNo: '116',
      internet: 'KH'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Cameroon',
      capital: 'Yaounde',
      fips: 'CM',
      iso2: 'CM',
      iso3: 'CMR',
      isoNo: '120',
      internet: 'CM'
    },
    {
      continent: 'Americas',
      region: 'North America',
      country: 'Canada',
      capital: 'Ottawa',
      fips: 'CA',
      iso2: 'CA',
      iso3: 'CAN',
      isoNo: '124',
      internet: 'CA'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Cape Verde',
      capital: 'Praia',
      fips: 'CV',
      iso2: 'CV',
      iso3: 'CPV',
      isoNo: '132',
      internet: 'CV'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Cayman Islands',
      capital: 'George Town',
      fips: 'CJ',
      iso2: 'KY',
      iso3: 'CYM',
      isoNo: '136',
      internet: 'KY'
    },
    {
      continent: 'Africa',
      region: 'Central Africa',
      country: 'Central African Republic',
      capital: 'Bangui',
      fips: 'CT',
      iso2: 'CF',
      iso3: 'CAF',
      isoNo: '140',
      internet: 'CF'
    },
    {
      continent: 'Africa',
      region: 'Central Africa',
      country: 'Chad',
      capital: "N'Djamena",
      fips: 'CD',
      iso2: 'TD',
      iso3: 'TCD',
      isoNo: '148',
      internet: 'TD'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Chile',
      capital: 'Santiago',
      fips: 'CI',
      iso2: 'CL',
      iso3: 'CHL',
      isoNo: '152',
      internet: 'CL'
    },
    {
      continent: 'Asia',
      region: 'East Asia',
      country: 'China',
      capital: 'Beijing',
      fips: 'CH',
      iso2: 'CN',
      iso3: 'CHN',
      isoNo: '156',
      internet: 'CN'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Christmas Island',
      capital: 'The Settlement',
      fips: 'KT',
      iso2: 'CX',
      iso3: 'CXR',
      isoNo: '162',
      internet: 'CX'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Cocos (Keeling) Islands',
      capital: 'West Island',
      fips: 'CK',
      iso2: 'CC',
      iso3: 'CCK',
      isoNo: '166',
      internet: 'CC'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Colombia',
      capital: 'Bogota',
      fips: 'CO',
      iso2: 'CO',
      iso3: 'COL',
      isoNo: '170',
      internet: 'CO'
    },
    {
      continent: 'Africa',
      region: 'Indian Ocean',
      country: 'Comoros',
      capital: 'Moroni',
      fips: 'CN',
      iso2: 'KM',
      iso3: 'COM',
      isoNo: '174',
      internet: 'KM'
    },
    {
      continent: 'Africa',
      region: 'Central Africa',
      country: 'Republic of the Congo',
      capital: 'Brazzaville',
      fips: 'CF',
      iso2: 'CG',
      iso3: 'COG',
      isoNo: '178',
      internet: 'CG'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Cook Islands',
      capital: 'Avarua',
      fips: 'CW',
      iso2: 'CK',
      iso3: 'COK',
      isoNo: '184',
      internet: 'CK'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'Costa Rica',
      capital: 'San Jose',
      fips: 'CS',
      iso2: 'CR',
      iso3: 'CRI',
      isoNo: '188',
      internet: 'CR'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: "Cote d'Ivoire",
      capital: 'Yamoussoukro',
      fips: 'IV',
      iso2: 'CI',
      iso3: 'CIV',
      isoNo: '384',
      internet: 'CI'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Croatia',
      capital: 'Zagreb',
      fips: 'HR',
      iso2: 'HR',
      iso3: 'HRV',
      isoNo: '191',
      internet: 'HR'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Cuba',
      capital: 'Havana',
      fips: 'CU',
      iso2: 'CU',
      iso3: 'CUB',
      isoNo: '192',
      internet: 'CU'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Cyprus',
      capital: 'Nicosia',
      fips: 'CY',
      iso2: 'CY',
      iso3: 'CYP',
      isoNo: '196',
      internet: 'CY'
    },
    {
      continent: 'Europe',
      region: 'Central Europe',
      country: 'Czechia',
      capital: 'Prague',
      fips: 'EZ',
      iso2: 'CZ',
      iso3: 'CZE',
      isoNo: '203',
      internet: 'CZ'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Denmark',
      capital: 'Copenhagen',
      fips: 'DA',
      iso2: 'DK',
      iso3: 'DNK',
      isoNo: '208',
      internet: 'DK'
    },
    {
      continent: 'Africa',
      region: 'Eastern Africa',
      country: 'Djibouti',
      capital: 'Djibouti',
      fips: 'DJ',
      iso2: 'DJ',
      iso3: 'DJI',
      isoNo: '262',
      internet: 'DJ'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Dominica',
      capital: 'Roseau',
      fips: 'DO',
      iso2: 'DM',
      iso3: 'DMA',
      isoNo: '212',
      internet: 'DM'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Dominican Republic',
      capital: 'Santo Domingo',
      fips: 'DR',
      iso2: 'DO',
      iso3: 'DOM',
      isoNo: '214',
      internet: 'DO'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Ecuador',
      capital: 'Quito',
      fips: 'EC',
      iso2: 'EC',
      iso3: 'ECU',
      isoNo: '218',
      internet: 'EC'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'Egypt',
      capital: 'Cairo',
      fips: 'EG',
      iso2: 'EG',
      iso3: 'EGY',
      isoNo: '818',
      internet: 'EG'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'El Salvador',
      capital: 'San Salvador',
      fips: 'ES',
      iso2: 'SV',
      iso3: 'SLV',
      isoNo: '222',
      internet: 'SV'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Equatorial Guinea',
      capital: 'Malabo',
      fips: 'EK',
      iso2: 'GQ',
      iso3: 'GNQ',
      isoNo: '226',
      internet: 'GQ'
    },
    {
      continent: 'Africa',
      region: 'Eastern Africa',
      country: 'Eritrea',
      capital: 'Asmara',
      fips: 'ER',
      iso2: 'ER',
      iso3: 'ERI',
      isoNo: '232',
      internet: 'ER'
    },
    {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: 'Estonia',
      capital: 'Tallinn',
      fips: 'EN',
      iso2: 'EE',
      iso3: 'EST',
      isoNo: '233',
      internet: 'EE'
    },
    {
      continent: 'Africa',
      region: 'Eastern Africa',
      country: 'Ethiopia',
      capital: 'Addis Ababa',
      fips: 'ET',
      iso2: 'ET',
      iso3: 'ETH',
      isoNo: '231',
      internet: 'ET'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Falkland Islands (Islas Malvinas)',
      capital: 'Stanley',
      fips: 'FA',
      iso2: 'FK',
      iso3: 'FLK',
      isoNo: '238',
      internet: 'FK'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Faroe Islands',
      capital: 'Torshavn',
      fips: 'FO',
      iso2: 'FO',
      iso3: 'FRO',
      isoNo: '234',
      internet: 'FO'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Fiji',
      capital: 'Suva',
      fips: 'FJ',
      iso2: 'FJ',
      iso3: 'FJI',
      isoNo: '242',
      internet: 'FJ'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Finland',
      capital: 'Helsinki',
      fips: 'FI',
      iso2: 'FI',
      iso3: 'FIN',
      isoNo: '246',
      internet: 'FI'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'France',
      capital: 'Paris',
      fips: 'FR',
      iso2: 'FR',
      iso3: 'FRA',
      isoNo: '250',
      internet: 'FR'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'French Guiana',
      capital: 'Cayenne',
      fips: 'FG',
      iso2: 'GF',
      iso3: 'GUF',
      isoNo: '254',
      internet: 'GF'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'French Polynesia',
      capital: 'Papeete',
      fips: 'FP',
      iso2: 'PF',
      iso3: 'PYF',
      isoNo: '258',
      internet: 'PF'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Gabon',
      capital: 'Libreville',
      fips: 'GB',
      iso2: 'GA',
      iso3: 'GAB',
      isoNo: '266',
      internet: 'GA'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'The Gambia',
      capital: 'Banjul',
      fips: 'GA',
      iso2: 'GM',
      iso3: 'GMB',
      isoNo: '270',
      internet: 'GM'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Georgia',
      capital: "T'bilisi",
      fips: 'GG',
      iso2: 'GE',
      iso3: 'GEO',
      isoNo: '268',
      internet: 'GE'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Germany',
      capital: 'Berlin',
      fips: 'GM',
      iso2: 'DE',
      iso3: 'DEU',
      isoNo: '276',
      internet: 'DE'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Ghana',
      capital: 'Accra',
      fips: 'GH',
      iso2: 'GH',
      iso3: 'GHA',
      isoNo: '288',
      internet: 'GH'
    },
    {
      continent: 'Europe',
      region: 'South West Europe',
      country: 'Gibraltar',
      capital: 'Gibraltar',
      fips: 'GI',
      iso2: 'GI',
      iso3: 'GIB',
      isoNo: '292',
      internet: 'GI'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Greece',
      capital: 'Athens',
      fips: 'GR',
      iso2: 'GR',
      iso3: 'GRC',
      isoNo: '300',
      internet: 'GR'
    },
    {
      continent: 'Americas',
      region: 'North America',
      country: 'Greenland',
      capital: 'Nuuk (Godthab)',
      fips: 'GL',
      iso2: 'GL',
      iso3: 'GRL',
      isoNo: '304',
      internet: 'GL'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Grenada',
      capital: "Saint George's",
      fips: 'GJ',
      iso2: 'GD',
      iso3: 'GRD',
      isoNo: '308',
      internet: 'GD'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Guadeloupe',
      capital: 'Basse-Terre',
      fips: 'GP',
      iso2: 'GP',
      iso3: 'GLP',
      isoNo: '312',
      internet: 'GP'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Guam',
      capital: 'Hagatna (Agana)',
      fips: 'GQ',
      iso2: 'GU',
      iso3: 'GUM',
      isoNo: '316',
      internet: 'GU'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'Guatemala',
      capital: 'Guatemala',
      fips: 'GT',
      iso2: 'GT',
      iso3: 'GTM',
      isoNo: '320',
      internet: 'GT'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Guinea',
      capital: 'Conakry',
      fips: 'GV',
      iso2: 'GN',
      iso3: 'GIN',
      isoNo: '324',
      internet: 'GN'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Guinea-Bissau',
      capital: 'Bissau',
      fips: 'PU',
      iso2: 'GW',
      iso3: 'GNB',
      isoNo: '624',
      internet: 'GW'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Guyana',
      capital: 'Georgetown',
      fips: 'GY',
      iso2: 'GY',
      iso3: 'GUY',
      isoNo: '328',
      internet: 'GY'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Haiti',
      capital: 'Port-au-Prince',
      fips: 'HA',
      iso2: 'HT',
      iso3: 'HTI',
      isoNo: '332',
      internet: 'HT'
    },
    {
      continent: 'Europe',
      region: 'Southern Europe',
      country: 'Holy See (Vatican City)',
      capital: 'Vatican City',
      fips: 'VT',
      iso2: 'VA',
      iso3: 'VAT',
      isoNo: '336',
      internet: 'VA'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'Honduras',
      capital: 'Tegucigalpa',
      fips: 'HO',
      iso2: 'HN',
      iso3: 'HND',
      isoNo: '340',
      internet: 'HN'
    },
    {
      continent: 'Europe',
      region: 'Central Europe',
      country: 'Hungary',
      capital: 'Budapest',
      fips: 'HU',
      iso2: 'HU',
      iso3: 'HUN',
      isoNo: '348',
      internet: 'HU'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Iceland',
      capital: 'Reykjavik',
      fips: 'IC',
      iso2: 'IS',
      iso3: 'ISL',
      isoNo: '352',
      internet: 'IS'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'India',
      capital: 'New Delhi',
      fips: 'IN',
      iso2: 'IN',
      iso3: 'IND',
      isoNo: '356',
      internet: 'IN'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Indonesia',
      capital: 'Jakarta',
      fips: 'ID',
      iso2: 'ID',
      iso3: 'IDN',
      isoNo: '360',
      internet: 'ID'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Iran',
      capital: 'Tehran',
      fips: 'IR',
      iso2: 'IR',
      iso3: 'IRN',
      isoNo: '364',
      internet: 'IR'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Iraq',
      capital: 'Baghdad',
      fips: 'IZ',
      iso2: 'IQ',
      iso3: 'IRQ',
      isoNo: '368',
      internet: 'IQ'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Ireland',
      capital: 'Dublin',
      fips: 'EI',
      iso2: 'IE',
      iso3: 'IRL',
      isoNo: '372',
      internet: 'IE'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Israel',
      capital: 'Jerusalem',
      fips: 'IS',
      iso2: 'IL',
      iso3: 'ISR',
      isoNo: '376',
      internet: 'IL'
    },
    {
      continent: 'Europe',
      region: 'Southern Europe',
      country: 'Italy',
      capital: 'Rome',
      fips: 'IT',
      iso2: 'IT',
      iso3: 'ITA',
      isoNo: '380',
      internet: 'IT'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Jamaica',
      capital: 'Kingston',
      fips: 'JM',
      iso2: 'JM',
      iso3: 'JAM',
      isoNo: '388',
      internet: 'JM'
    },
    {
      continent: 'Asia',
      region: 'East Asia',
      country: 'Japan',
      capital: 'Tokyo',
      fips: 'JA',
      iso2: 'JP',
      iso3: 'JPN',
      isoNo: '392',
      internet: 'JP'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Jordan',
      capital: 'Amman',
      fips: 'JO',
      iso2: 'JO',
      iso3: 'JOR',
      isoNo: '400',
      internet: 'JO'
    },
    {
      continent: 'Asia',
      region: 'Central Asia',
      country: 'Kazakhstan',
      capital: 'Astana (Akmola)',
      fips: 'KZ',
      iso2: 'KZ',
      iso3: 'KAZ',
      isoNo: '398',
      internet: 'KZ'
    },
    {
      continent: 'Africa',
      region: 'Eastern Africa',
      country: 'Kenya',
      capital: 'Nairobi',
      fips: 'KE',
      iso2: 'KE',
      iso3: 'KEN',
      isoNo: '404',
      internet: 'KE'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Kiribati',
      capital: 'Tarawa',
      fips: 'KR',
      iso2: 'KI',
      iso3: 'KIR',
      isoNo: '296',
      internet: 'KI'
    },
    {
      continent: 'Asia',
      region: 'East Asia',
      country: 'North Korea',
      capital: "P'yongyang",
      fips: 'KN',
      iso2: 'KP',
      iso3: 'PRK',
      isoNo: '408',
      internet: 'KP'
    },
    {
      continent: 'Asia',
      region: 'East Asia',
      country: 'South Korea',
      capital: 'Seoul',
      fips: 'KS',
      iso2: 'KR',
      iso3: 'KOR',
      isoNo: '410',
      internet: 'KR'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Kuwait',
      capital: 'Kuwait',
      fips: 'KU',
      iso2: 'KW',
      iso3: 'KWT',
      isoNo: '414',
      internet: 'KW'
    },
    {
      continent: 'Asia',
      region: 'Central Asia',
      country: 'Kyrgyzstan',
      capital: 'Bishkek',
      fips: 'KG',
      iso2: 'KG',
      iso3: 'KGZ',
      isoNo: '417',
      internet: 'KG'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Laos',
      capital: 'Vientiane',
      fips: 'LA',
      iso2: 'LA',
      iso3: 'LAO',
      isoNo: '418',
      internet: 'LA'
    },
    {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: 'Latvia',
      capital: 'Riga',
      fips: 'LG',
      iso2: 'LV',
      iso3: 'LVA',
      isoNo: '428',
      internet: 'LV'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Lebanon',
      capital: 'Beirut',
      fips: 'LE',
      iso2: 'LB',
      iso3: 'LBN',
      isoNo: '422',
      internet: 'LB'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Lesotho',
      capital: 'Maseru',
      fips: 'LT',
      iso2: 'LS',
      iso3: 'LSO',
      isoNo: '426',
      internet: 'LS'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Liberia',
      capital: 'Monrovia',
      fips: 'LI',
      iso2: 'LR',
      iso3: 'LBR',
      isoNo: '430',
      internet: 'LR'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'Libya',
      capital: 'Tripoli',
      fips: 'LY',
      iso2: 'LY',
      iso3: 'LBY',
      isoNo: '434',
      internet: 'LY'
    },
    {
      continent: 'Europe',
      region: 'Central Europe',
      country: 'Liechtenstein',
      capital: 'Vaduz',
      fips: 'LS',
      iso2: 'LI',
      iso3: 'LIE',
      isoNo: '438',
      internet: 'LI'
    },
    {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: 'Lithuania',
      capital: 'Vilnius',
      fips: 'LH',
      iso2: 'LT',
      iso3: 'LTU',
      isoNo: '440',
      internet: 'LT'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Luxembourg',
      capital: 'Luxembourg',
      fips: 'LU',
      iso2: 'LU',
      iso3: 'LUX',
      isoNo: '442',
      internet: 'LU'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'North Macedonia',
      capital: 'Skopje',
      fips: 'MK',
      iso2: 'MK',
      iso3: 'MKD',
      isoNo: '807',
      internet: 'MK'
    },
    {
      continent: 'Africa',
      region: 'Indian Ocean',
      country: 'Madagascar',
      capital: 'Antananarivo',
      fips: 'MA',
      iso2: 'MG',
      iso3: 'MDG',
      isoNo: '450',
      internet: 'MG'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Malawi',
      capital: 'Lilongwe',
      fips: 'MI',
      iso2: 'MW',
      iso3: 'MWI',
      isoNo: '454',
      internet: 'MW'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Malaysia',
      capital: 'Kuala Lumpur',
      fips: 'MY',
      iso2: 'MY',
      iso3: 'MYS',
      isoNo: '458',
      internet: 'MY'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'Maldives',
      capital: 'Male (Maale)',
      fips: 'MV',
      iso2: 'MV',
      iso3: 'MDV',
      isoNo: '462',
      internet: 'MV'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Mali',
      capital: 'Bamako',
      fips: 'ML',
      iso2: 'ML',
      iso3: 'MLI',
      isoNo: '466',
      internet: 'ML'
    },
    {
      continent: 'Europe',
      region: 'Southern Europe',
      country: 'Malta',
      capital: 'Valletta',
      fips: 'MT',
      iso2: 'MT',
      iso3: 'MLT',
      isoNo: '470',
      internet: 'MT'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Isle of Man',
      capital: 'Douglas',
      fips: 'IM',
      iso2: 'IM',
      iso3: 'IMN',
      isoNo: '833',
      internet: 'IM'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Marshall Islands',
      capital: 'Majuro',
      fips: 'RM',
      iso2: 'MH',
      iso3: 'MHL',
      isoNo: '584',
      internet: 'MH'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Martinique',
      capital: 'Fort-de-France',
      fips: 'MB',
      iso2: 'MQ',
      iso3: 'MTQ',
      isoNo: '474',
      internet: 'MQ'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Mauritania',
      capital: 'Nouakchott',
      fips: 'MR',
      iso2: 'MR',
      iso3: 'MRT',
      isoNo: '478',
      internet: 'MR'
    },
    {
      continent: 'Africa',
      region: 'Indian Ocean',
      country: 'Mauritius',
      capital: 'Port Louis',
      fips: 'MP',
      iso2: 'MU',
      iso3: 'MUS',
      isoNo: '480',
      internet: 'MU'
    },
    {
      continent: 'Africa',
      region: 'Indian Ocean',
      country: 'Mayotte',
      capital: 'Mamoutzou',
      fips: 'MF',
      iso2: 'YT',
      iso3: 'MYT',
      isoNo: '175',
      internet: 'YT'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'Mexico',
      capital: 'Mexico',
      fips: 'MX',
      iso2: 'MX',
      iso3: 'MEX',
      isoNo: '484',
      internet: 'MX'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Federated States of Micronesia',
      capital: 'Palikir',
      fips: '',
      iso2: 'FM',
      iso3: 'FSM',
      isoNo: '583',
      internet: 'FM'
    },
    {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: 'Moldova',
      capital: 'Chisinau',
      fips: 'MD',
      iso2: 'MD',
      iso3: 'MDA',
      isoNo: '498',
      internet: 'MD'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Monaco',
      capital: 'Monaco',
      fips: 'MN',
      iso2: 'MC',
      iso3: 'MCO',
      isoNo: '492',
      internet: 'MC'
    },
    {
      continent: 'Asia',
      region: 'Northern Asia',
      country: 'Mongolia',
      capital: 'Ulaanbaatar',
      fips: 'MG',
      iso2: 'MN',
      iso3: 'MNG',
      isoNo: '496',
      internet: 'MN'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Montserrat',
      capital: 'Plymouth',
      fips: 'MH',
      iso2: 'MS',
      iso3: 'MSR',
      isoNo: '500',
      internet: 'MS'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'Morocco',
      capital: 'Rabat',
      fips: 'MO',
      iso2: 'MA',
      iso3: 'MAR',
      isoNo: '504',
      internet: 'MA'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Mozambique',
      capital: 'Maputo',
      fips: 'MZ',
      iso2: 'MZ',
      iso3: 'MOZ',
      isoNo: '508',
      internet: 'MZ'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Myanmar (Burma)',
      capital: 'Rangoon (Yangon)',
      fips: 'BM',
      iso2: 'MM',
      iso3: 'MMR',
      isoNo: '104',
      internet: 'MM'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Namibia',
      capital: 'Windhoek',
      fips: 'WA',
      iso2: 'NA',
      iso3: 'NAM',
      isoNo: '516',
      internet: 'NA'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Nauru',
      capital: 'no official capital',
      fips: 'NR',
      iso2: 'NR',
      iso3: 'NRU',
      isoNo: '520',
      internet: 'NR'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'Nepal',
      capital: 'Kathmandu',
      fips: 'NP',
      iso2: 'NP',
      iso3: 'NPL',
      isoNo: '524',
      internet: 'NP'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Netherlands',
      capital: 'Amsterdam',
      fips: 'NL',
      iso2: 'NL',
      iso3: 'NLD',
      isoNo: '528',
      internet: 'NL'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Netherlands Antilles',
      capital: 'Willemstad',
      fips: 'NT',
      iso2: 'AN',
      iso3: 'ANT',
      isoNo: '530',
      internet: 'AN'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'New Caledonia',
      capital: 'Noumea',
      fips: 'NC',
      iso2: 'NC',
      iso3: 'NCL',
      isoNo: '540',
      internet: 'NC'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'New Zealand',
      capital: 'Wellington',
      fips: 'NZ',
      iso2: 'NZ',
      iso3: 'NZL',
      isoNo: '554',
      internet: 'NZ'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'Nicaragua',
      capital: 'Managua',
      fips: 'NU',
      iso2: 'NI',
      iso3: 'NIC',
      isoNo: '558',
      internet: 'NI'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Niger',
      capital: 'Niamey',
      fips: 'NG',
      iso2: 'NE',
      iso3: 'NER',
      isoNo: '562',
      internet: 'NE'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Nigeria',
      capital: 'Abuja',
      fips: 'NI',
      iso2: 'NG',
      iso3: 'NGA',
      isoNo: '566',
      internet: 'NG'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Niue',
      capital: 'Alofi',
      fips: 'NE',
      iso2: 'NU',
      iso3: 'NIU',
      isoNo: '570',
      internet: 'NU'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Norfolk Island',
      capital: 'Kingston',
      fips: 'NF',
      iso2: 'NF',
      iso3: 'NFK',
      isoNo: '574',
      internet: 'NF'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Northern Mariana Islands',
      capital: 'Saipan',
      fips: 'CQ',
      iso2: 'MP',
      iso3: 'MNP',
      isoNo: '580',
      internet: 'MP'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Norway',
      capital: 'Oslo',
      fips: 'NO',
      iso2: 'NO',
      iso3: 'NOR',
      isoNo: '578',
      internet: 'NO'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Oman',
      capital: 'Muscat',
      fips: 'MU',
      iso2: 'OM',
      iso3: 'OMN',
      isoNo: '512',
      internet: 'OM'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'Pakistan',
      capital: 'Islamabad',
      fips: 'PK',
      iso2: 'PK',
      iso3: 'PAK',
      isoNo: '586',
      internet: 'PK'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Palau',
      capital: 'Koror',
      fips: 'PS',
      iso2: 'PW',
      iso3: 'PLW',
      isoNo: '585',
      internet: 'PW'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Palestinian Territory',
      capital: 'East Jerusalem',
      fips: 'WE',
      iso2: 'PS',
      iso3: 'PSE',
      isoNo: '275',
      internet: 'PS'
    },
    {
      continent: 'Americas',
      region: 'Central America',
      country: 'Panama',
      capital: 'Panama',
      fips: 'PM',
      iso2: 'PA',
      iso3: 'PAN',
      isoNo: '591',
      internet: 'PA'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Papua New Guinea',
      capital: 'Port Moresby',
      fips: 'PP',
      iso2: 'PG',
      iso3: 'PNG',
      isoNo: '598',
      internet: 'PG'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Paraguay',
      capital: 'Asuncion',
      fips: 'PA',
      iso2: 'PY',
      iso3: 'PRY',
      isoNo: '600',
      internet: 'PY'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Peru',
      capital: 'Lima',
      fips: 'PE',
      iso2: 'PE',
      iso3: 'PER',
      isoNo: '604',
      internet: 'PE'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Philippines',
      capital: 'Manila',
      fips: 'RP',
      iso2: 'PH',
      iso3: 'PHL',
      isoNo: '608',
      internet: 'PH'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Pitcairn Islands',
      capital: 'Adamstown',
      fips: 'PC',
      iso2: 'PN',
      iso3: 'PCN',
      isoNo: '612',
      internet: 'PN'
    },
    {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: 'Poland',
      capital: 'Warsaw',
      fips: 'PL',
      iso2: 'PL',
      iso3: 'POL',
      isoNo: '616',
      internet: 'PL'
    },
    {
      continent: 'Europe',
      region: 'South West Europe',
      country: 'Portugal',
      capital: 'Lisbon',
      fips: 'PO',
      iso2: 'PT',
      iso3: 'PRT',
      isoNo: '620',
      internet: 'PT'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Puerto Rico',
      capital: 'San Juan',
      fips: 'RQ',
      iso2: 'PR',
      iso3: 'PRI',
      isoNo: '630',
      internet: 'PR'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Qatar',
      capital: 'Doha',
      fips: 'QA',
      iso2: 'QA',
      iso3: 'QAT',
      isoNo: '634',
      internet: 'QA'
    },
    {
      continent: 'Africa',
      region: 'Indian Ocean',
      country: 'Reunion',
      capital: 'Saint-Denis',
      fips: 'RE',
      iso2: 'RE',
      iso3: 'REU',
      isoNo: '638',
      internet: 'RE'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Romania',
      capital: 'Bucharest',
      fips: 'RO',
      iso2: 'RO',
      iso3: 'ROU',
      isoNo: '642',
      internet: 'RO'
    },
    {
      continent: 'Asia',
      region: 'Northern Asia',
      country: 'Russia',
      capital: 'Moscow',
      fips: 'RS',
      iso2: 'RU',
      iso3: 'RUS',
      isoNo: '643',
      internet: 'RU'
    },
    {
      continent: 'Africa',
      region: 'Central Africa',
      country: 'Rwanda',
      capital: 'Kigali',
      fips: 'RW',
      iso2: 'RW',
      iso3: 'RWA',
      isoNo: '646',
      internet: 'RW'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Saint Kitts and Nevis',
      capital: 'Basseterre',
      fips: 'SC',
      iso2: 'KN',
      iso3: 'KNA',
      isoNo: '659',
      internet: 'KN'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Saint Lucia',
      capital: 'Castries',
      fips: 'ST',
      iso2: 'LC',
      iso3: 'LCA',
      isoNo: '662',
      internet: 'LC'
    },
    {
      continent: 'Americas',
      region: 'North America',
      country: 'Saint Pierre and Miquelon',
      capital: 'Saint-Pierre',
      fips: 'SB',
      iso2: 'PM',
      iso3: 'SPM',
      isoNo: '666',
      internet: 'PM'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Saint Vincent and the Grenadines',
      capital: 'Kingstown',
      fips: 'VC',
      iso2: 'VC',
      iso3: 'VCT',
      isoNo: '670',
      internet: 'VC'
    },
    {
      continent: 'Europe',
      region: 'Southern Europe',
      country: 'San Marino',
      capital: 'San Marino',
      fips: 'SM',
      iso2: 'SM',
      iso3: 'SMR',
      isoNo: '674',
      internet: 'SM'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Sao Tome and Principe',
      capital: 'Sao Tome',
      fips: 'TP',
      iso2: 'ST',
      iso3: 'STP',
      isoNo: '678',
      internet: 'ST'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Saudi Arabia',
      capital: 'Riyadh',
      fips: 'SA',
      iso2: 'SA',
      iso3: 'SAU',
      isoNo: '682',
      internet: 'SA'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Senegal',
      capital: 'Dakar',
      fips: 'SG',
      iso2: 'SN',
      iso3: 'SEN',
      isoNo: '686',
      internet: 'SN'
    },
    {
      continent: 'Africa',
      region: 'Indian Ocean',
      country: 'Seychelles',
      capital: 'Victoria',
      fips: 'SE',
      iso2: 'SC',
      iso3: 'SYC',
      isoNo: '690',
      internet: 'SC'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Sierra Leone',
      capital: 'Freetown',
      fips: 'SL',
      iso2: 'SL',
      iso3: 'SLE',
      isoNo: '694',
      internet: 'SL'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Singapore',
      capital: 'Singapore',
      fips: 'SN',
      iso2: 'SG',
      iso3: 'SGP',
      isoNo: '702',
      internet: 'SG'
    },
    {
      continent: 'Europe',
      region: 'Central Europe',
      country: 'Slovakia',
      capital: 'Bratislava',
      fips: 'LO',
      iso2: 'SK',
      iso3: 'SVK',
      isoNo: '703',
      internet: 'SK'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Slovenia',
      capital: 'Ljubljana',
      fips: 'SI',
      iso2: 'SI',
      iso3: 'SVN',
      isoNo: '705',
      internet: 'SI'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Solomon Islands',
      capital: 'Honiara',
      fips: 'BP',
      iso2: 'SB',
      iso3: 'SLB',
      isoNo: '090',
      internet: 'SB'
    },
    {
      continent: 'Africa',
      region: 'Eastern Africa',
      country: 'Somalia',
      capital: 'Mogadishu',
      fips: 'SO',
      iso2: 'SO',
      iso3: 'SOM',
      isoNo: '706',
      internet: 'SO'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'South Africa',
      capital: 'Pretoria��',
      fips: 'SF',
      iso2: 'ZA',
      iso3: 'ZAF',
      isoNo: '710',
      internet: 'ZA'
    },
    {
      continent: 'Europe',
      region: 'South West Europe',
      country: 'Spain',
      capital: 'Madrid',
      fips: 'SP',
      iso2: 'ES',
      iso3: 'ESP',
      isoNo: '724',
      internet: 'ES'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'Sri Lanka',
      capital: 'Colombo',
      fips: 'CE',
      iso2: 'LK',
      iso3: 'LKA',
      isoNo: '144',
      internet: 'LK'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'Sudan',
      capital: 'Khartoum',
      fips: 'SU',
      iso2: 'SD',
      iso3: 'SDN',
      isoNo: '736',
      internet: 'SD'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Suriname',
      capital: 'Paramaribo',
      fips: 'NS',
      iso2: 'SR',
      iso3: 'SUR',
      isoNo: '740',
      internet: 'SR'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Svalbard',
      capital: 'Longyearbyen',
      fips: 'SV',
      iso2: 'SJ',
      iso3: 'SJM',
      isoNo: '744',
      internet: 'SJ'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Eswatini',
      capital: 'Mbabane',
      fips: 'WZ',
      iso2: 'SZ',
      iso3: 'SWZ',
      isoNo: '748',
      internet: 'SZ'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Sweden',
      capital: 'Stockholm',
      fips: 'SW',
      iso2: 'SE',
      iso3: 'SWE',
      isoNo: '752',
      internet: 'SE'
    },
    {
      continent: 'Europe',
      region: 'Central Europe',
      country: 'Switzerland',
      capital: 'Bern',
      fips: 'SZ',
      iso2: 'CH',
      iso3: 'CHE',
      isoNo: '756',
      internet: 'CH'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Syria',
      capital: 'Damascus',
      fips: 'SY',
      iso2: 'SY',
      iso3: 'SYR',
      isoNo: '760',
      internet: 'SY'
    },
    {
      continent: 'Asia',
      region: 'East Asia',
      country: 'Taiwan',
      capital: 'Taipei',
      fips: 'TW',
      iso2: 'TW',
      iso3: 'TWN',
      isoNo: '158',
      internet: 'TW'
    },
    {
      continent: 'Asia',
      region: 'Central Asia',
      country: 'Tajikistan',
      capital: 'Dushanbe',
      fips: 'TI',
      iso2: 'TJ',
      iso3: 'TJK',
      isoNo: '762',
      internet: 'TJ'
    },
    {
      continent: 'Africa',
      region: 'Eastern Africa',
      country: 'Tanzania',
      capital: 'Dar es Salaam',
      fips: 'TZ',
      iso2: 'TZ',
      iso3: 'TZA',
      isoNo: '834',
      internet: 'TZ'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Thailand',
      capital: 'Bangkok',
      fips: 'TH',
      iso2: 'TH',
      iso3: 'THA',
      isoNo: '764',
      internet: 'TH'
    },
    {
      continent: 'Africa',
      region: 'Western Africa',
      country: 'Togo',
      capital: 'Lome',
      fips: 'TO',
      iso2: 'TG',
      iso3: 'TGO',
      isoNo: '768',
      internet: 'TG'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Tokelau',
      capital: 'none',
      fips: 'TL',
      iso2: 'TK',
      iso3: 'TKL',
      isoNo: '772',
      internet: 'TK'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Tonga',
      capital: "Nuku'alofa",
      fips: 'TN',
      iso2: 'TO',
      iso3: 'TON',
      isoNo: '776',
      internet: 'TO'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Trinidad and Tobago',
      capital: 'Port-of-Spain',
      fips: 'TD',
      iso2: 'TT',
      iso3: 'TTO',
      isoNo: '780',
      internet: 'TT'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'Tunisia',
      capital: 'Tunis',
      fips: 'TS',
      iso2: 'TN',
      iso3: 'TUN',
      isoNo: '788',
      internet: 'TN'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Turkey',
      capital: 'Ankara',
      fips: 'TU',
      iso2: 'TR',
      iso3: 'TUR',
      isoNo: '792',
      internet: 'TR'
    },
    {
      continent: 'Asia',
      region: 'Central Asia',
      country: 'Turkmenistan',
      capital: 'Ashgabat',
      fips: 'TX',
      iso2: 'TM',
      iso3: 'TKM',
      isoNo: '795',
      internet: 'TM'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Turks and Caicos Islands',
      capital: 'Grand Turk',
      fips: 'TK',
      iso2: 'TC',
      iso3: 'TCA',
      isoNo: '796',
      internet: 'TC'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Tuvalu',
      capital: 'Funafuti',
      fips: 'TV',
      iso2: 'TV',
      iso3: 'TUV',
      isoNo: '798',
      internet: 'TV'
    },
    {
      continent: 'Africa',
      region: 'Eastern Africa',
      country: 'Uganda',
      capital: 'Kampala',
      fips: 'UG',
      iso2: 'UG',
      iso3: 'UGA',
      isoNo: '800',
      internet: 'UG'
    },
    {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: 'Ukraine',
      capital: 'Kyiv',
      fips: 'UP',
      iso2: 'UA',
      iso3: 'UKR',
      isoNo: '804',
      internet: 'UA'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'United Arab Emirates',
      capital: 'Abu Dhabi',
      fips: 'TC',
      iso2: 'AE',
      iso3: 'ARE',
      isoNo: '784',
      internet: 'AE'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'United Kingdom',
      capital: 'London',
      fips: 'UK',
      iso2: 'GB',
      iso3: 'GBR',
      isoNo: '826',
      internet: 'UK'
    },
    {
      continent: 'Americas',
      region: 'North America',
      country: 'United States',
      capital: 'Washington DC',
      fips: 'US',
      iso2: 'US',
      iso3: 'USA',
      isoNo: '840',
      internet: 'US'
    },
    {
      continent: 'Americas',
      region: 'North America',
      country: 'United States Minor Outlying Islands',
      capital: 'Washington DC',
      fips: '',
      iso2: 'UM',
      iso3: 'UMI',
      isoNo: '581',
      internet: 'US'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Uruguay',
      capital: 'Montevideo',
      fips: 'UY',
      iso2: 'UY',
      iso3: 'URY',
      isoNo: '858',
      internet: 'UY'
    },
    {
      continent: 'Asia',
      region: 'Central Asia',
      country: 'Uzbekistan',
      capital: 'Tashkent (Toshkent)',
      fips: 'UZ',
      iso2: 'UZ',
      iso3: 'UZB',
      isoNo: '860',
      internet: 'UZ'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Vanuatu',
      capital: 'Port-Vila',
      fips: 'NH',
      iso2: 'VU',
      iso3: 'VUT',
      isoNo: '548',
      internet: 'VU'
    },
    {
      continent: 'Americas',
      region: 'South America',
      country: 'Venezuela',
      capital: 'Caracas',
      fips: 'VE',
      iso2: 'VE',
      iso3: 'VEN',
      isoNo: '862',
      internet: 'UE'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Vietnam',
      capital: 'Hanoi',
      fips: 'VN',
      iso2: 'VN',
      iso3: 'VNM',
      isoNo: '704',
      internet: 'VN'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Virgin Islands',
      capital: 'Charlotte Amalie',
      fips: 'VQ',
      iso2: 'VI',
      iso3: 'VIR',
      isoNo: '850',
      internet: 'VI'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Wallis and Futuna',
      capital: 'Mata-Utu (on Ile Uvea)',
      fips: 'WF',
      iso2: 'WF',
      iso3: 'WLF',
      isoNo: '876',
      internet: 'WF'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'Western Sahara',
      capital: 'none',
      fips: 'WI',
      iso2: 'EH',
      iso3: 'ESH',
      isoNo: '732',
      internet: 'EH'
    },
    {
      continent: 'Oceania',
      region: 'Pacific',
      country: 'Western Samoa',
      capital: 'Apia',
      fips: 'WS',
      iso2: 'WS',
      iso3: 'WSM',
      isoNo: '882',
      internet: 'WS'
    },
    {
      continent: 'Asia',
      region: 'South West Asia',
      country: 'Yemen',
      capital: 'Sanaa',
      fips: 'YM',
      iso2: 'YE',
      iso3: 'YEM',
      isoNo: '887',
      internet: 'YE'
    },
    {
      continent: 'Africa',
      region: 'Central Africa',
      country: 'Democratic Republic of the Congo',
      capital: 'Kinshasa',
      fips: 'CG',
      iso2: 'CD',
      iso3: 'COD',
      isoNo: '180',
      internet: 'ZR'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Zambia',
      capital: 'Lusaka',
      fips: 'ZA',
      iso2: 'ZM',
      iso3: 'ZMB',
      isoNo: '894',
      internet: 'ZM'
    },
    {
      continent: 'Africa',
      region: 'Southern Africa',
      country: 'Zimbabwe',
      capital: 'Harare',
      fips: 'ZI',
      iso2: 'ZW',
      iso3: 'ZWE',
      isoNo: '716',
      internet: 'ZW'
    },
    {
      continent: 'Asia',
      region: 'East Asia',
      country: 'Hong Kong',
      capital: 'Victoria',
      fips: 'HK',
      iso2: 'HK',
      iso3: 'HKG',
      isoNo: '344',
      internet: 'HK'
    },
    {
      continent: 'Asia',
      region: 'East Asia',
      country: 'Macau',
      capital: 'Macau',
      fips: 'MC',
      iso2: 'MO',
      iso3: 'MAC',
      isoNo: '446',
      internet: 'MO'
    },
    {
      continent: 'Antarctica',
      region: 'Antarctica',
      country: 'Antarctica',
      capital: '',
      fips: 'AY',
      iso2: 'AQ',
      iso3: 'ATA',
      isoNo: '010',
      internet: 'AQ'
    },
    {
      continent: 'Atlantic Ocean',
      region: 'South Atlantic Ocean',
      country: 'Bouvet Island',
      capital: '',
      fips: 'BV',
      iso2: 'BV',
      iso3: 'BVT',
      isoNo: '074',
      internet: 'BV'
    },
    {
      continent: 'Asia',
      region: 'South Asia',
      country: 'British Indian Ocean Territory',
      capital: '',
      fips: 'IO',
      iso2: 'IO',
      iso3: 'IOT',
      isoNo: '086',
      internet: 'IO'
    },
    {
      continent: 'Indian Ocean',
      region: 'Southern Indian Ocean',
      country: 'French Southern and Antarctic Lands',
      capital: '',
      fips: 'FS',
      iso2: 'TF',
      iso3: 'ATF',
      isoNo: '260',
      internet: '--'
    },
    {
      continent: 'Indian Ocean',
      region: 'Southern Indian Ocean',
      country: 'Heard Island and McDonald Islands',
      capital: '',
      fips: 'HM',
      iso2: 'HM',
      iso3: 'HMD',
      isoNo: '334',
      internet: 'HM'
    },
    {
      continent: 'Atlantic Ocean',
      region: 'South Atlantic Ocean',
      country: 'Saint Helena',
      capital: '',
      fips: 'SH',
      iso2: 'SH',
      iso3: 'SHN',
      isoNo: '654',
      internet: 'SH'
    },
    {
      continent: 'Atlantic Ocean',
      region: 'South Atlantic Ocean',
      country: 'South Georgia and the South Sandwich Islands',
      capital: '',
      fips: 'SX',
      iso2: 'GS',
      iso3: 'SGS',
      isoNo: '239',
      internet: 'GS'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Guernsey',
      capital: 'Saint Peter Port',
      fips: 'GK',
      iso2: 'GG',
      iso3: 'GGY',
      isoNo: '831',
      internet: 'GG'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Serbia',
      capital: 'Belgrade',
      fips: 'RI',
      iso2: 'RS',
      iso3: 'SRB',
      isoNo: '688',
      internet: 'RS'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Saint Barthélemy',
      capital: 'Gustavia',
      fips: 'TB',
      iso2: 'BL',
      iso3: 'BLM',
      isoNo: '652',
      internet: 'BL'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Montenegro',
      capital: 'Podgorica',
      fips: 'MJ',
      iso2: 'ME',
      iso3: 'MNE',
      isoNo: '499',
      internet: 'ME'
    },
    {
      continent: 'Europe',
      region: 'Western Europe',
      country: 'Jersey',
      capital: 'Saint Helier',
      fips: 'JE',
      iso2: 'JE',
      iso3: 'JEY',
      isoNo: '832',
      internet: 'JE'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Curaçao',
      capital: 'Willemstad',
      fips: 'UC',
      iso2: 'CW',
      iso3: 'CUW',
      isoNo: '531',
      internet: 'CW'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Saint Martin',
      capital: 'Marigot',
      fips: 'RN',
      iso2: 'MF',
      iso3: 'MAF',
      isoNo: '663',
      internet: 'MF'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Sint Maarten',
      capital: 'Philipsburg',
      fips: 'NN',
      iso2: 'SX',
      iso3: 'SXM',
      isoNo: '534',
      internet: 'SX'
    },
    {
      continent: 'Asia',
      region: 'South East Asia',
      country: 'Timor-Leste',
      capital: 'Dili',
      fips: 'TT',
      iso2: 'TL',
      iso3: 'TLS',
      isoNo: '626',
      internet: 'TL'
    },
    {
      continent: 'Africa',
      region: 'Northern Africa',
      country: 'South Sudan',
      capital: 'Juba',
      fips: 'OD',
      iso2: 'SS',
      iso3: 'SSD',
      isoNo: '728',
      internet: 'SS'
    },
    {
      continent: 'Europe',
      region: 'Northern Europe',
      country: 'Åland Islands',
      capital: 'Mariehamn',
      fips: 'AX',
      iso2: 'AX',
      iso3: 'ALA',
      isoNo: '248',
      internet: 'AX'
    },
    {
      continent: 'Americas',
      region: 'West Indies',
      country: 'Bonaire',
      capital: 'Kralendijk',
      fips: 'BQ',
      iso2: 'BQ',
      iso3: 'BES',
      isoNo: '535',
      internet: 'BQ'
    },
    {
      continent: 'Europe',
      region: 'South East Europe',
      country: 'Republic of Kosovo',
      capital: 'Pristina',
      fips: 'KV',
      iso2: 'XK',
      iso3: 'XKS',
      isoNo: null,
      internet: 'XK'
    },
];

const EUROPEAN_COUNTRIES = [
    {
        continent: 'Europe',
        region: 'South East Europe',
        country: 'Albania',
        capital: 'Tirana',
        localName: 'Shqipëri'
    },
    {
        continent: 'Europe',
        region: 'Western Europe',
        country: 'Austria',
        capital: 'Vienna',
        localName: 'Österreich'
    },
    {
        continent: 'Europe',
        region: 'Eastern Europe',
        country: 'Belarus',
        capital: 'Minsk',
        localName: 'Беларусь'
    },
    {
        continent: 'Europe',
        region: 'Western Europe',
        country: 'Belgium',
        capital: 'Brussels',
        localName: 'België' // Dutch, Belgique in French, Belgien in German
    },
    {
        continent: 'Europe',
        region: 'South East Europe',
        country: 'Bosnia and Herzegovina',
        capital: 'Sarajevo',
        localName: 'Bosna i Hercegovina'
    },
    {
        continent: 'Europe',
        region: 'Eastern Europe',
        country: 'Bulgaria',
        capital: 'Sofia',
        localName: 'България'
    },
    {
        continent: 'Europe',
        region: 'Southern Europe',
        country: 'Croatia',
        capital: 'Zagreb',
        localName: 'Hrvatska'
    },
    {
        continent: 'Europe',
        region: 'Southern Europe',
        country: 'Cyprus',
        capital: 'Nicosia',
        localName: 'Κύπρος' // Greek, Kıbrıs in Turkish
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Denmark',
        capital: 'Copenhagen',
        localName: 'Danmark'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Estonia',
        capital: 'Tallinn',
        localName: 'Eesti'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Finland',
        capital: 'Helsinki',
        localName: 'Suomi'
    },
    {
        continent: 'Europe',
        region: 'Western Europe',
        country: 'France',
        capital: 'Paris',
        localName: 'France'
    },
    {
        continent: 'Europe',
        region: 'Western Europe',
        country: 'Germany',
        capital: 'Berlin',
        localName: 'Deutschland'
    },
    {
        continent: 'Europe',
        region: 'Southern Europe',
        country: 'Greece',
        capital: 'Athens',
        localName: 'Ελλάδα'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Iceland',
        capital: 'Reykjavik',
        localName: 'Ísland'
    },
    {
        continent: 'Europe',
        region: 'Southern Europe',
        country: 'Italy',
        capital: 'Rome',
        localName: 'Italia'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Latvia',
        capital: 'Riga',
        localName: 'Latvija'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Lithuania',
        capital: 'Vilnius',
        localName: 'Lietuva'
    },
    {
        continent: 'Europe',
        region: 'Western Europe',
        country: 'Luxembourg',
        capital: 'Luxembourg',
        localName: 'Lëtzebuerg' // Luxembourgish, Luxemburg in German, Luxembourg in French
    },
    {
        continent: 'Europe',
        region: 'Southern Europe',
        country: 'Malta',
        capital: 'Valletta',
        localName: 'Malta'
    },
    {
        continent: 'Europe',
        region: 'Eastern Europe',
        country: 'Moldova',
        capital: 'Chisinau',
        localName: 'Moldova'
    },
    {
        continent: 'Europe',
        region: 'Western Europe',
        country: 'Monaco',
        capital: 'Monaco',
        localName: 'Monaco'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Netherlands',
        capital: 'Amsterdam',
        localName: 'Nederland'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Norway',
        capital: 'Oslo',
        localName: 'Norge' // Bokmål, Noreg in Nynorsk
    },
    {
        continent: 'Europe',
        region: 'Eastern Europe',
        country: 'Poland',
        capital: 'Warsaw',
        localName: 'Polska'
    },
    {
        continent: 'Europe',
        region: 'Southern Europe',
        country: 'Portugal',
        capital: 'Lisbon',
        localName: 'Portugal'
    },
    {
        continent: 'Europe',
        region: 'Eastern Europe',
        country: 'Romania',
        capital: 'Bucharest',
        localName: 'România'
    },
    {
        continent: 'Europe',
        region: 'Eastern Europe',
        country: 'Russia',
        capital: 'Moscow',
        localName: 'Россия'
    },
    {
        continent: 'Europe',
        region: 'Northern Europe',
        country: 'Sweden',
        capital: 'Stockholm',
        localName: 'Sverige'
    },
    {
        continent: 'Europe',
        region: 'Central Europe',
        country: 'Switzerland',
        capital: 'Bern',
        localName: 'Schweiz' // German, Suisse in French, Svizzera in Italian, Svizra in Romansh
    },
    {
        continent: 'Europe',
        region: 'Eastern Europe',
        country: 'Ukraine',
        capital: 'Kyiv',
        localName: 'Україна'
    },
    {
        continent: 'Europe',
        region: 'Southern Europe',
        country: 'Spain',
        capital: 'Madrid',
        localName: 'España'
    },
    // Add more European countries as needed
];

const MARKDOWN_OPTIONS = {
  pedantic: false,
  gfm: true,
  breaks: true,
  sanitize: false,
  smartLists: true,
  smartypants: true,
  xhtml: true,
};

const KCAL_KG_KM = 1.25;
const ECOR = i => KCAL_KG_KM + i / 100 * 9.81 * (45.6 + 1.16 * i) / 100;
const AVG_DOG_WEIGHT = 25;
const FREE_RUNNING_KM_PER_HOUR = 2.0;

const INTERSECTION_ELEMENTS = {};
const INTERSECTION_HISTORY = [];

function onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
}

class LocalUtil {
    static getDayColor(day, darkmode) {
        if (RED_DAYS.indexOf(day) > -1) {
            if (darkmode) {
                return '#501515';
            }
            return '#ffdddd';
        }
        return '';
    }

    static getRankingColor(rank) {
        return RANKING_COLORS[rank - 1];
    }

    static getTrainingColor(summary, darkmode) {
        if (summary.distanceKm > 0 || summary.distance > 0) {
            if (darkmode) {
                return '#154015';
            }
            return '#aaffaa';
        } else if (summary.duration > 0) {
            if (darkmode) {
                return '#303030';
            }
            return '#aaaaaa';
        }
        return '';
    }

    static getPlanColor(summary, darkmode) {
        if (summary.distanceKm > 0 || summary.distance > 0) {
            if (darkmode) {
                return '#0052cc';
            }
            return '#b3d1ff';
        }
        return '';
    }

    static getHistoryTypeColor(historyType) {
        const colorName = HISTORY_TYPE_COLOR_MAP[historyType];
        if (typeof colorName  === 'number') {
            return colorName
        }
        return COLOR_NAMES[colorName];
    }

    static avgArr(array) {
        if (Array.isArray(array) && array.length > 0) {
            return array.reduce((a, b) => a + b) / array.length;
        }
        return 0;
    }

    static getNextMonth(month, year) {
        const result = { month, year };
        if ((month + 1) > 12) {
            result.month = 1;
            result.year += 1;
        } else {
            result.month += 1;
        }
        return result;
    }

    static getPrevMonth(month, year) {
        const result = { month, year };
        if ((month - 1) < 1) {
            result.month = 12;
            result.year -= 1;
        } else {
            result.month -= 1;
        }
        return result;
    }

    static getNextWeek(week, year) {
        const result = { week, year };
        if ((week + 1) > 53) {
            result.week = 1;
            result.year += 1;
        } else {
            result.week += 1;
        }
        return result;
    }

    static getPrevWeek(week, year) {
        const result = { week, year };
        if ((week - 1) < 1) {
            result.week = 53;
            result.year -= 1;
        } else {
            result.week -= 1;
        }
        return result;
    }

    static getHistoryMap(lang = 'no') {
        if (!HISTORY_TYPE_MAP[lang]) {
            return HISTORY_TYPE_MAP.en;
        }
        return HISTORY_TYPE_MAP[lang];
    }

    static getVaccineTypes() {
        return VACCINE_TYPES;
    }

    static getVaccineTypesVet() {
        return VACCINE_TYPES_VETERINARY;
    }

    static getVaccineAgensVet() {
        return VACCINE_AGENS_VETERINARY;
    }

    static getHistoryPri(num) {
        switch (num) {
            case 1:
                return HISTORY_PRIORITY_LIST_1;
            case 2:
                return HISTORY_PRIORITY_LIST_2;
            case 3:
                return HISTORY_PRIORITY_LIST_3;
            default:
                return HISTORY_PRIORITY_LIST_1;
        }
    }

    static speedAvg(obj) {
        let speedAvg = 0;
        if (obj.duration > 0 && obj.distanceKm > 0) {
            const rest = obj.rest || 0;
            const duration = obj.duration - rest;
            speedAvg = obj.distanceKm / (duration / 3600);
        }
        return speedAvg;
    }

    static getDay(dow, lang = 'en', full = false) {
        if (full) {
            if (!DAYS_FULL[lang]) {
                return DAYS_FULL.en[dow];
            }
            return DAYS_FULL[lang][dow];
        }
        if (!DAYS_SHORT[lang]) {
            return DAYS_SHORT.en[dow];
        }
        return DAYS_SHORT[lang][dow];
    }

    static getDays(lang = 'no', full = false) {
        if (full) {
            if (!DAYS_FULL[lang]) {
                return DAYS_FULL.en;
            }
            return DAYS_FULL[lang];
        }
        if (!DAYS[lang]) {
            return DAYS.en;
        }
        return DAYS[lang];
    }

    static getMonths(lang = 'no') {
        if (!MONTHS[lang]) {
            return MONTHS.en;
        }
        return MONTHS[lang];
    }

    static getMonth(month, lang = 'en') {
        if (!MONTHS[lang]) {
            return MONTHS.en[month - 1];
        }
        return MONTHS[lang][month - 1];
    }

    static marginTop(hasSubMenu = false, hasSubMenuTabs = false, app = false) {
        if (app) {
            if (hasSubMenuTabs) {
                return APP_MARGIN_TOP_W_SUBMENU_TABS;
            }
            if (hasSubMenu) {
                return APP_MARGIN_TOP_W_SUBMENU;
            }
            return APP_MARGIN_TOP;
        }

        if (hasSubMenuTabs) {
            return APP_MARGIN_TOP_W_SUBMENU_TABS;
        }
        if (hasSubMenu) {
            return APP_MARGIN_TOP_W_SUBMENU;
        }
        return APP_MARGIN_TOP;
    }

    static marginTopBack(hasSubMenu = false, hasSubMenuTabs = false, app = false) {
        if (app) {
            if (hasSubMenuTabs) {
                return APP_MARGIN_TOP_BACK_W_SUBMENU_TABS;
            }
            if (hasSubMenu) {
                return APP_MARGIN_TOP_BACK_W_SUBMENU;
            }
            return APP_MARGIN_TOP_BACK;
        }
        if (hasSubMenuTabs) {
            return APP_MARGIN_TOP_BACK_W_SUBMENU_TABS;
        }
        if (hasSubMenu) {
            return APP_MARGIN_TOP_BACK_W_SUBMENU;
        }
        return APP_MARGIN_TOP_BACK;
    }

    static marginBottom() {
        return MARGIN_BOTTOM;
    }

    static subMenuMarginTop(scrolledDown, app = false) {
        if (app) {
            return APP_SUB_MENU_MARGIN_TOP;
        }
        return APP_SUB_MENU_MARGIN_TOP;
    }

    static windDirection(deg) {
        if (deg>11.25 && deg<33.75) {
            return "NNE";
        } else if (deg>33.75 && deg<56.25) {
            return "ENE";
        } else if (deg>56.25 && deg<78.75) {
            return "E";
        } else if (deg>78.75 && deg<101.25) {
            return "ESE";
        } else if (deg>101.25 && deg<123.75) {
            return "ESE";
        } else if (deg>123.75 && deg<146.25) {
            return "SE";
        } else if (deg>146.25 && deg<168.75) {
            return "SSE";
        } else if (deg>168.75 && deg<191.25) {
            return "S";
        } else if (deg>191.25 && deg<213.75) {
            return "SSW";
        } else if (deg>213.75 && deg<236.25) {
            return "SW";
        } else if (deg>236.25 && deg<258.75) {
            return "WSW";
        } else if (deg>258.75 && deg<281.25) {
            return "W";
        } else if (deg>281.25 && deg<303.75) {
            return "WNW";
        } else if (deg>303.75 && deg<326.25) {
            return "NW";
        } else if (deg>326.25 && deg<348.75) {
            return "NNW";
        }
        return "N";
    }

    static getTemperatureColor(temp) {
        if (temp <= 0) {
            return 'text-primary';
        }
        if (temp <= 10) {
            return 'text-success';
        }
        return 'text-danger';
    }

    static getWindSpeedColor(speed) {
        if (speed > 10) {
            return 'text-warning';
        }
        if (speed > 20) {
            return 'text-danger';
        }
        return '';
    }

    static avg(arr) {
        return arr.reduce((a, b) => a + b, 0) / arr.length;
    }

    static smoothOut(arr, variance) {
        const tAvg = LocalUtil.avg(arr) * variance;
        const ret = [];
        for (let i = 0; i < arr.length; i += 1) {
            const prev = i > 0 ? ret[i - 1] : arr[i];
            const next = i < arr.length ? arr[i] : arr[i - 1];
            ret[i] = LocalUtil.avg([tAvg, LocalUtil.avg([prev, arr[i], next])]);
        }
        return ret;
    }

    static smoothArray(array, smoothing) {
        const newArray = [];
        for (let i = 0, l = array.length; i < l; i += 1) {
            let sum = 0;

            for (let index = i - smoothing; index <= i + smoothing; index += 1) {
                const thisIndex = index < 0 ? index + array.length : index % array.length;
                sum += array[thisIndex];
            }
            newArray[i] = sum / ((smoothing * 2) + 1);
        }
        return newArray;
    }

    static maxLength(string, max, link = '') {
        if (typeof string === 'string') {
            const length = string.length;
            if (length > max) {
                const cutPos = string.substr(0, max).lastIndexOf(' ');
                return `${string.substr(0, cutPos)}... ${link}`;
            }
            return string;
        }
        return string;
    }

    static nl2p($str) {
        if (typeof $str === 'string') {
            return $str.split('\n\n');
        }
        return [$str];
    }

    static nl2br($str) {
        if (typeof $str === 'string') {
            return $str.split('\n');
        }
        return [$str];
    }

    static calcEcor(climbPercent, distanceKm = 1) {
        return ECOR(climbPercent) * distanceKm;
    }

    static calcKcal(distanceKm = 1, duration) {
        if (distanceKm > 0) {
            return KCAL_KG_KM * AVG_DOG_WEIGHT * distanceKm;
        } else if (duration > 0) {
            // TODO: Calculate energy usage on freerunning.
            const totalHours = duration / 3600;
            const distanceKmPrHour = FREE_RUNNING_KM_PER_HOUR;
            const totalDistanceSimulated = totalHours * distanceKmPrHour;
            return KCAL_KG_KM * AVG_DOG_WEIGHT * totalDistanceSimulated;
        }
        return 0;
    }

    static getLoadIndexText(loadIndexNormalized, language = 'en') {
        if (loadIndexNormalized >= 300) {
            return 'Epic';
        }
        if (loadIndexNormalized >= 200) {
            return 'Extreme';
        }
        if (loadIndexNormalized >= 150) {
            return 'Massive';
        }
        if (loadIndexNormalized >= 100) {
            return 'Tough';
        }
        if (loadIndexNormalized >= 80) {
            return 'Very hard';
        }
        if (loadIndexNormalized >= 70) {
            return 'Hard';
        }
        if (loadIndexNormalized >= 60) {
            return 'Above normal';
        }
        if (loadIndexNormalized > 40) {
            return 'Normal';
        }
        if (loadIndexNormalized > 30) {
            return 'Easy';
        }
        return 'Very easy';
    }

    static getLoadIndexColor(loadIndexNormalized, language = 'en') {
        if (loadIndexNormalized >= 100) {
            return 'danger';
        }
        if (loadIndexNormalized >= 80) {
            return 'primary';
        }
        if (loadIndexNormalized >= 70) {
            return 'primary';
        }
        if (loadIndexNormalized >= 60) {
            return 'success';
        }
        if (loadIndexNormalized > 40) {
            return 'success';
        }
        if (loadIndexNormalized > 30) {
            return 'info';
        }
        return 'primary';
    }

    static getMaxLoadIndex() {
        return MAX_LOADINDEX;
    }

    static getNormalSpeed() {
        return NORMAL_SPEED_KMT;
    }

    static getElevationThreshold() {
        return ELEVATION_THRESHOLD;
    }

    static getWindowSize() {
        return WINDOW_SIZE_ARRAY_SMOOTH;
    }

    static displayNameShort(user = {}) {
        let displayNameShort;
        if (!user) {
            return '';
        }
        if (util.isString(user.firstname) && user.firstname !== '') {
            displayNameShort = `${util.ucfirst(user.firstname, true)}${util.ucfirst(user.lastname, true)}`;
        } else if (util.isString(user.name) && user.name !== '') {
            const parts = user.name.split(/[^A-Za-z]/);
            displayNameShort = `${util.ucfirst(parts[0] || '', true)}${util.ucfirst(parts[1] || '', true)}`;
        } else if (util.isString(user.username) && user.username !== '') {
            const parts = user.username.split(/[^A-Za-z]/);
            displayNameShort = `${util.ucfirst(parts[0] || '', true)}${util.ucfirst(parts[1] || '', true)}`;
        } else if (util.isString(user.email)) {
            const parts = user.email.split(/[^A-Za-z0-9]/);
            displayNameShort = `${util.ucfirst(parts[0] || '', true)}${util.ucfirst(parts[1] || '', true)}`;
        } else if (util.isString(user.title)) {
            const parts = user.title.split(/[^A-Za-z0-9]/);
            displayNameShort = `${util.ucfirst(parts[0] || '', true)}${util.ucfirst(parts[1] || '', true)}`;
        }
        return displayNameShort;
    }

    static displayName(user = {}, showFullname) {
        if (!user) {
            return '';
        }
        if (!showFullname && util.isString(user.username) && user.username !== '') {
            return user.username;
        }
        if (util.isString(user.firstname) && user.firstname !== '') {
            return `${user.firstname} ${user.lastname}`;
        }
        if (util.isString(user.name) && user.name !== '') {
            return `${user.name}`;
        }
        if (util.isString(user.email)) {
            const firstPartOfEmail = user.email.replace(/^(.+?)@.+$/, '$1');
            const parts = firstPartOfEmail.split(/[^A-Za-z0-9]/);
            return `${util.ucfirst(parts[0] || '')} ${util.ucfirst(parts[1] || '')}`;
        }
        if (util.isString(user.cellphone)) {
            const cellphone = user.cellphone.replace(/^\+47/g, '');
            const cellphoneFormatted = cellphone.replace(/(\d{3})(\d{2})(\d{3})/, '$1 $2 $3');
            return cellphoneFormatted;
        }
        return '';
    }

    static showName(obj, nameOnly = false, useFirstnameLastname = true) {
        if (!obj) {
            return 'n/a';
        }
        let { firstname, lastname, name } = obj;

        if (firstname) {
            firstname = firstname.trim();
        }
        if (lastname) {
            lastname = lastname.trim();
        }

        if (useFirstnameLastname) {
            if (firstname || lastname) {
                return `${firstname || ''} ${lastname || ''}`.trim();
            }
            if (name) {
                name = name.trim();
                return name;
            }
            return '--- ---';
        }

        if (name) {
            name = name.trim();
            if (!nameOnly && (lastname || firstname)) {
                return `${name} (${firstname || ''} ${lastname || ''})`;
            }
            return name;
        }
        return `${firstname || '---'} ${lastname || '---'}`;
    }

    static youtubeReplacer(match, p0, p1, p2, p3) {
        // console.log('youtubeReplacer', 'match', match, 'p1', p1, 'p2', p2, 'p3', p3, 'p4', p4);
        let result = `<div class='d-flex'>
            <div class='w-100' style='position: relative; width: 100%; padding-bottom: 56.25%;'>
                <iframe
                    style='position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;'
                    src='https://www.youtube-nocookie.com/embed/${p3}?rel=0'
                    webkitallowfullscreen mozallowfullscreen allowfullscreen
                ></iframe>
            </div>
        </div>`;
        // let result = `
        //     <video
        //         controls='controls'
        //         preload='none'
        //     >
        //         <source src='https://www.youtube.com/watch?v=${p3}' type='video/mp4' />
        //     </video>
        // `;
        result = result.replace(/\n/g, '');
        return result;
    }

    static replaceInlineContent($content) {
        let content = $content;
        if (typeof content !== 'string') {
            return content;
        }
        // youtube https://www.youtube.com/shorts/GeDRUM0QI2U
        if (content.match(/youtube/) && content.match(/\/shorts\//)) {
            // eslint-disable-next-line
            const youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtube\.com\/shorts\/(([^&\s<$]+)(&[^\s<$]+)*)/gi;
            content = content.replace(youtubeRegex, LocalUtil.youtubeReplacer);
        } else if (content.match(/youtube/) && content.match(/\/live\//)) {
            // eslint-disable-next-line
            const youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtube\.com\/live\/(([^&\s<$]+)(&[^\s<$]+)*)/gi;
            content = content.replace(youtubeRegex, LocalUtil.youtubeReplacer);
        } else if (content.match(/youtube/)) {
            // eslint-disable-next-line
            const youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtube\.com\/(.*?v=([^&\s<$]+)(&[^\s<$]+)*)/gi;
            content = content.replace(youtubeRegex, LocalUtil.youtubeReplacer);
        // } else if (content.match(/youtu\.be/)) {
        //     // eslint-disable-next-line
        //     const youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtu\.be\/(([^?&\s<$]+)((\?|&)[^\s<$]+)*)/gi;
        //     content = content.replace(youtubeRegex, LocalUtil.youtubeReplacer);
        }
        return content;
    }

    static getInlineComponents(body) {
        if (typeof body !== 'string') {
            return body;
        }
        const components = [];

        function BBreplacer(match, p1, p2, p3) {
            // p1 = tag
            // p2 = params
            // p3 = content
            const tag = p1.toLowerCase();
            const params = p2.split(' ');
            const opts = {};
            const content = p3.trim();
            const contentLines = content.split('\n');
            params.forEach((e) => {
                const param = e.split('=');
                if (param[0] && param[1]) {
                    opts[param[0]] = param[1].replace(/;/g, ' ');
                }
            });
            components.push({
                name: tag,
                params,
                opts,
                content,
            })
            return '';
        }

        const bbRegexp = /\[([a-z]+)(.*?)\](.*?(?=\[\/\1\]))\[\/\1\]/gs;
        const finalBody = body.replace(bbRegexp, BBreplacer);

        return { finalBody, components };
    }

    static replaceImages(line = '', images, language = 'en', showTextSize = false) {
        function imageReplacer(match, p1, p2, p3, offset, string) {
            // console.log({ match, p1, p2, p3, offset, string })
            if (util.isObject(images[p1])) {
                return `<img src=${images[p1].s3XXLargeLink} class='img-fluid ${p3 ? p3 : 'float-right'}' style='width: ${p2 || 40}%;' />`;
            }
            return '';
        }
        function imageFieldReplacer(match, p1, p2, p3, offset, string) {
            // console.log({ match, p1, p2, p3, offset, string })
            if (util.isObject(images[p1])) {
                return `${images[p1][p2]}`;
            }
            return '';
        }

        function tagReplacer(match, p1, p2, p3, offset, string) {
            if (p1) {
                return `<a href='/tags/${p2}'>${p1}</a>`;
            }
            return string;
        }

        if (util.isString(line)) {
            const isOnlyEmojis = LocalUtil.isOnlyEmojis(line);
            const lineLength = line.length;
            let text = line;
            const regex = new RegExp(`<${language}>(.*?)</${language}>`, 's');
            const match = regex.exec(text);
            if (match) {
                const parsedLanguageText = match[1];
                if (parsedLanguageText) {
                    text = parsedLanguageText;
                }
            }
            let textWithImagesPart1 = text.replace(/\{\{img\.(\d+)\s*(\d*)\s*([a-z\-\s]*)\}\}/g, imageReplacer);
            let textWithImages = textWithImagesPart1.replace(/\{\{img\.(\d+).([a-z\-\s]*)\}\}/g, imageFieldReplacer);
            if (showTextSize) {
                if (isOnlyEmojis) {
                    textWithImages = `<div style='font-size: 3.0em; line-height:1.1em;'>${textWithImages}</span>`;
                } else if (lineLength <= 25) {
                    textWithImages = `<div style='font-size: 1.5em; line-height:1.5em;'>${textWithImages}</span>`;
                }
            }
            const textWithTags = textWithImages.replace(/(#([0-9a-zæøå]+))/gi, tagReplacer);
            // return textWithTags;
            const textWithVideos = LocalUtil.replaceInlineContent(textWithTags);
            return textWithVideos;
        }
        return line;
    }

    static replaceFieldTags(line = '') {
        const tags = [];
        function tagReplacer(match, p1, p2, p3, offset, string) {
            if (p1) {
                const singleTags = p1.split(',').map(e => e.trim());
                tags.push(singleTags);
            }
            return '';
        }
        line.replace(/<!--(.+?)-->/gi, tagReplacer);
        const tagObjects = [];
        tags.flat().forEach((tag) => {
            const tagParts = tag.split(';');
            tagObjects.push({
                name: tagParts[0],
                title: tagParts[1],
                type: tagParts[2],
                help: tagParts[3],
            });
        });
        return tagObjects;
    }

    // static getImage({ user, team, object, size = 's3SmallLink', fallback, field = 'image', fieldPlural = 'images', lastImage }) {
    //     let image = {};
    //     // eslint-disable-next-line no-lonely-if
    //     if (team && team[field] && team[field].s3SmallLink) {
    //         image = team[field];
    //     } else if (team && team[fieldPlural] && team[fieldPlural][0] && team[fieldPlural][0].s3SmallLink) {
    //         image = team[fieldPlural][0];
    //     } else if (user && user[field] && user[field].s3SmallLink) {
    //         image = user[field];
    //     } else if (user && user[fieldPlural] && user[fieldPlural][0] && user[fieldPlural][0].s3SmallLink) {
    //         if (lastImage) {
    //             image = user[fieldPlural][user[fieldPlural].length - 1];
    //         } else {
    //             image = user[fieldPlural][0];
    //         }
    //     } else if (object && object[field] && object[field].s3SmallLink) {
    //         image = object[field];
    //     } else if (object && object[fieldPlural] && object[fieldPlural][0] && object[fieldPlural][0].s3SmallLink) {
    //         image = object[fieldPlural][0];
    //     }

    //     if (image && image[size]) {
    //         return image[size];
    //     } else if (fallback) {
    //         return fallback;
    //     }
    // }
    static hasImage({ user, field = 'image', fieldPlural = 'images' }) {
        if (user[field] && user[field].s3SmallLink) {
            return true;
        }
        if (user[fieldPlural] && user[fieldPlural].length > 0 && user[fieldPlural][0].s3SmallLink) {
            return true;
        }
        return false;
    }

    static getImage({ user, team, object, size = 's3SmallLink', fallback, field = 'image', fieldPlural = 'images', lastImage }) {
        let image = {};

        // Check for the image in team
        if (team) {
            if (team[field] && team[field].s3SmallLink) {
                image = team[field];
            } else if (team[fieldPlural] && team[fieldPlural].length > 0 && team[fieldPlural][0].s3SmallLink) {
                image = team[fieldPlural][0];
            }
        }

        // Check for the image in user if not found in team
        if (!image.s3SmallLink && user) {
            if (user[field] && user[field].s3SmallLink) {
                image = user[field];
            } else if (user[fieldPlural] && user[fieldPlural].length > 0 && user[fieldPlural][0].s3SmallLink) {
                image = lastImage ? user[fieldPlural][user[fieldPlural].length - 1] : user[fieldPlural][0];
            }
        }

        // Check for the image in object if not found in user or team
        if (!image.s3SmallLink && object) {
            if (object[field] && object[field].s3SmallLink) {
                image = object[field];
            } else if (object[fieldPlural] && object[fieldPlural].length > 0 && object[fieldPlural][0].s3SmallLink) {
                image = object[fieldPlural][0];
            }
        }

        // Return the image in the specified size or the fallback
        return image[size] || fallback;
    }

    static getMarkdownOptions() {
        return MARKDOWN_OPTIONS;
    }

    static asUrl(input = '') {
        if (input.match(/^http/)) {
            return input;
        }
        return `https://${input}`;
    }

    static asFacebookLink(input = '') {
        if (input.match(/facebook.com/)) {
            if (input.match(/^http/)) {
                return input;
            }
            return `https://${input}`;
        }
        return `https://facebook.com/${input}`;
    }

    static asInstagramLink(input) {
        if (input.match(/instagram.com/)) {
            if (input.match(/^http/)) {
                return input;
            }
            return `https://${input}`;
        }
        return `https://instagram.com/${input}`;
    }

    static asSnapchatLink(input) {
        if (input.match(/snapchat.com/)) {
            if (input.match(/^http/)) {
                return input;
            }
            return `https://${input}`;
        }
        return `https://www.snapchat.com/add/${input}`;
    }

    static randomIntFromInterval(min, max) { // min and max included
        return Math.floor(Math.random() * (max - min + 1) + min);
    }

    static resizeTextarea(el) {
        let element = el;
            // TODO: Make data-attribute for max-height.
        if (element.target) {
        element = el.target;
        }
        if (element && element.style) {
            // Reset field height
            element.dataset.originalHeight = element.dataset.originalHeight || `${element.offsetHeight}px`;
            element.style.height = element.dataset.originalHeight;

            // Get the computed styles for the element
            const computed = window.getComputedStyle(element);

            // Calculate the height
            let height = parseInt(computed.getPropertyValue('border-top-width'), 10)
                // + parseInt(computed.getPropertyValue('padding-top'), 10)
                + element.scrollHeight
                // + parseInt(computed.getPropertyValue('padding-bottom'), 10)
                + parseInt(computed.getPropertyValue('border-bottom-width'), 10);

            if (height > 1024) {
                height = 1024;
            }

            element.style.height = `${height}px`;
            element.style.maxHeight = `40vh`;
        }
  }

    static restoreSizeTextarea(e) {
        let element = e;
        if (element.target) {
            element = e.target;
        }
        if (element && element.style) {
            element.style.height = element.dataset.originalHeight;
        }
    }

    static hasBirthday(date) {
        const today = new Date();
        const day = today.getDate();
        const month = today.getMonth();

        const currentDate = new Date(date);
        const currentDay = currentDate.getDate();
        const currentMonth = currentDate.getMonth();

        if (day === currentDay && month === currentMonth) {
            return true;
        }
        return false;
    }

    static deltaSec(from, to) {
        const fromDate = util.parseInputDate(from);
        const endDate = util.parseInputDate(to);
        const deltaMs = endDate.getTime() - fromDate.getTime();
        return Math.floor(deltaMs / 1000);
    }

    static addDeltaSec(inputDate, sec = 0) {
        const date = new Date(util.parseInputDate(inputDate));
        date.setSeconds(date.getSeconds() + sec);
        return date;
    }

    static formatDate(inputDate, $opts = {}) {
        const opts = { ...$opts };
        const locale = opts.locale || 'en-US';
        delete opts.locale;
        const options = {
            // dateStyle      "full"
            //                "long"
            //                "medium"
            //                "short"
            // timeStyle      "full"
            //                "long"
            //                "medium"
            //                "short"
            // localeMatcher  "best-fit" (default)
            //                "lookup"
            // timeZone
            // hour12         false
            //                true
            // hourCycle      "h11"
            //                "h12"
            //                "h23"
            //                "h24"
            // formatMatcher  "basic"
            //                "best-fit" (default)
            // weekday        "long"
            //                "short"
            //                "narrow"
            // year           "2-digit"
            //                "numeric"
            // month          "2-digit"
            //                "long"
            //                "narrow"
            //                "numeric"
            //                "short"
            // day            "2-digit"
            //                "long"
            // hour           "2-digit"
            //                "long"
            // minute         "2-digit"
            //                "long"
            // second         "2-digit"
            //                "long"
            // timeZoneName   "long"
            //                "short"
            ...opts,
        };
        const parsedDate = util.isDate(inputDate) ? inputDate : new Date(inputDate);
        return parsedDate.toLocaleString(locale, options);
    }

    static daysUntilNextBirthday(date) {
        if (!date) {
            return null;
        }
        const today = new Date();
        const year = today.getFullYear();

        const birthday = util.parseInputDate(date);
        const birthdayDay = birthday.getDate();
        const birthdayMonth = birthday.getMonth() + 1;

        const birthdayThisYear = util.parseInputDate(`${year}-${util.padDate(birthdayMonth)}-${util.padDate(birthdayDay)}`);
        const birthdayNextYear = util.parseInputDate(`${year + 1}-${util.padDate(birthdayMonth)}-${util.padDate(birthdayDay)}`);
        let nextBirthday = birthdayThisYear;
        if (today > birthdayThisYear) {
            nextBirthday = birthdayNextYear;
        }

        const daysUntilNextBirthday = (nextBirthday - today) / (24 * 60 * 60 * 1000);
        return daysUntilNextBirthday;
    }

    static getTrophyImage(name) {
        return TROPHY_IMAGES[name];
    }

    static isOnlyEmojis(text) {
        if (util.isString(text)) {
            const onlyEmojis = text.match(/\p{Extended_Pictographic}/gu) || [];
            const visibleChars = text.match(/[^\n\r\s\p{Extended_Pictographic}]/gu) || [];
            return onlyEmojis.length === visibleChars.length;
        }
        return false;
    }

    static intersectionObserverCallback(entries) {
        entries.forEach((e) => {
            const { id, type, title, user, team, sessionid } = e.target.dataset;
            const { intersectionRatio, isIntersecting, time } = e;
            const el = INTERSECTION_ELEMENTS[`${type}-${id}`];
            if (el && el.isIntersecting && !isIntersecting) {
                el.endTime = new Date().getTime();
                el.duration = el.endTime - el.startTime;
                const elToPush = { ...el };
                delete INTERSECTION_ELEMENTS[`${type}-${id}`];
                INTERSECTION_HISTORY.push(elToPush);
            } else {
                INTERSECTION_ELEMENTS[`${type}-${id}`] = {
                    startTime: new Date().getTime(),
                    elementId: parseInt(id, 10),
                    user: parseInt(user, 10),
                    team: parseInt(team, 10),
                    type,
                    intersectionRatio,
                    isIntersecting,
                    time,
                    sessionId: sessionid,
                };
            }

            // boundingClientRect: DOMRect { x: 0, y: 336.48333740234375, width: 1191, … }
            // intersectionRatio: 1
            // intersectionRect: DOMRect { x: 0, y: 336.48333740234375, width: 1191, … }
            // isIntersecting: true
            // rootBounds: DOMRect { x: 0, y: 0, width: 1206, … }
            // target: <div class="row pt-3 pb-3 bg-light" style="">
            // time: 26522.14
            // console.log({ id, title, intersectionRatio, isIntersecting, time });
        });
    }

    static getIntersectionObserverHistory() {
        const elements = INTERSECTION_HISTORY.splice(0, INTERSECTION_HISTORY.length);
        return elements;
    }

    static getViews(statistics = {}) {
        let views = 0;
        if (util.isObject(statistics)) {
            const years = Object.keys(statistics)
            for (let i = 0, l = years.length; i < l; i += 1) {
                const year = years[i];
                if (util.isObject(statistics[year])) {
                    const weeks = Object.keys(statistics[year]);
                    for (let j = 0, m = weeks.length; j < m; j += 1) {
                        const week = weeks[j];
                        const count = statistics[year][week].count;
                        if (util.isNumber(count)) {
                            views += count;
                        }
                    }
                }
            }
        }
        return views;
    }

    static getAdminData(statistics = {}) {
        let data = {
            count: 0,
        };
        if (util.isObject(statistics)) {
            const years = Object.keys(statistics).sort();
            for (let i = 0, l = years.length; i < l; i += 1) {
                const year = years[i];
                if (util.isObject(statistics[year])) {
                    const weeks = Object.keys(statistics[year]).sort();
                    for (let j = 0, m = weeks.length; j < m; j += 1) {
                        const week = weeks[j];
                        const count = statistics[year][week].count;
                        if (util.isNumber(count)) {
                            data.count += count;
                        }
                        const ranking = statistics[year][week].ranking;
                        if (util.isNumber(ranking)) {
                            data.rankingTrend = 0;
                            if (data.ranking && data.ranking < ranking) {
                                data.rankingTrend = -1;
                            }
                            if (data.ranking && data.ranking > ranking) {
                                data.rankingTrend = 1;
                            }
                            data.ranking = ranking;
                        }
                        const durationAvg = statistics[year][week].durationAvg;
                        if (util.isNumber(durationAvg)) {
                            data.durationAvgTrend = 0;
                            if (data.durationAvg && data.durationAvg < durationAvg) {
                                data.durationAvgTrend = -1;
                            }
                            if (data.durationAvg && data.durationAvg > durationAvg) {
                                data.durationAvgTrend = 1;
                            }
                            data.durationAvg = durationAvg;
                        }
                        const timeAvg = statistics[year][week].timeAvg;
                        if (util.isNumber(timeAvg)) {
                            data.timeAvgTrend = 0;
                            if (data.timeAvg && data.timeAvg < timeAvg) {
                                data.timeAvgTrend = -1;
                            }
                            if (data.timeAvg && data.timeAvg > timeAvg) {
                                data.timeAvgTrend = 1;
                            }
                            data.timeAvg = timeAvg;
                        }
                    }
                }
            }
        }
        return data;
    }

    static getTranslation({ object, targetLang, field }) {
        if (object && object.translations) {
            const idx = object.translations?.findIndex((e) => {
                return e.language === targetLang && e.field === field;
            });
            if (idx > -1) {
                return object.translations[idx].text;
            }
        }
        return null;
    }

    static getClassForTrend(trend) {
        if (trend === 1) {
            return 'fas fa-arrow-up text-success';
        }
        if (trend === -1) {
            return 'fas fa-arrow-down text-danger';
        }
        return 'fas fa-ellipsis-h text-secondary';
    }

    static randomInt(min, max) {
        return Math.floor(Math.random() * (max - min + 1) + min)
    }

    static captureEvents(e) {
        e.stopPropagation();
    }

    static captureAllEvents(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    static getGraphData(inputObject) {
        const legends = Object.keys(inputObject);
        const data = legends.map(e => util.getNestedValue(inputObject, e) || []);
        return {
            legends,
            data,
        };
    }

    static getFirstPoint(workout = {}) {
        let firstPoint;
        if (util.checkNested(workout, 'gpxInfo', 'points', '0')) {
            firstPoint = util.getNestedValue(workout, 'gpxInfo.points.0');
        }
        if (util.checkNested(workout, 'gpx', 'geoJSON', 'features', '0', 'geometry', 'coordinates', '0', '0', '0')) {
            const firstGpxPoint = util.getNestedValue(workout, 'gpx.geoJSON.features.0.geometry.coordinates.0.0');
            firstPoint = [firstGpxPoint[1], firstGpxPoint[0], firstGpxPoint[2]];
        } else if (util.checkNested(workout, 'gpx', 'geoJSON', 'features', '0', 'geometry', 'coordinates', '0', '0')) {
            const firstGpxPoint = util.getNestedValue(workout, 'gpx.geoJSON.features.0.geometry.coordinates.0');
            firstPoint = [firstGpxPoint[1], firstGpxPoint[0], firstGpxPoint[2]];
        }
        return firstPoint;
    }

    static getMidPoint(workout = {}, position = 0.5) {
        let midPoint;
        if (util.checkNested(workout, 'gpxInfo', 'points', '0')) {
            const array = util.getNestedValue(workout, `gpxInfo.points`);
            if (util.isArray(array)) {
                const length = array.length;
                const midIndex = Math.floor(position === 1 ? length - 1 : length * position);
                midPoint = util.getNestedValue(workout, `gpxInfo.points.${midIndex}`);
            }
        }
        if (util.checkNested(workout, 'gpx', 'geoJSON', 'features', '0', 'geometry', 'coordinates', '0', '0', '0')) {
            const array = util.getNestedValue(workout, `gpx.geoJSON.features.0.geometry.coordinates.0`);
            if (util.isArray(array)) {
                const length = array.length;
                const midIndex = Math.floor(position === 1 ? length - 1 : length * position);
                const midGpxPoint = util.getNestedValue(workout, `gpx.geoJSON.features.0.geometry.coordinates.0.${midIndex}`);
                midPoint = [midGpxPoint[1], midGpxPoint[0], midGpxPoint[2]];
            }
        } else if (util.checkNested(workout, 'gpx', 'geoJSON', 'features', '0', 'geometry', 'coordinates', '0', '0')) {
            // const array = util.getNestedValue(workout, `gpx.geoJSON.features.0.geometry.coordinates`);
            const array = util.getNestedValue(workout, `gpx.geoJSON.features.0.geometry.coordinates`);
            if (util.isArray(array)) {
                const length = array.length;
                const midIndex = Math.floor(position === 1 ? length - 1 : length * position);
                const midGpxPoint = util.getNestedValue(workout, `gpx.geoJSON.features.0.geometry.coordinates.${midIndex}`);
                midPoint = [midGpxPoint[1], midGpxPoint[0], midGpxPoint[2]];
            }
        }
        return midPoint;
    }

    static getLastValue(array, field, fallback) {
        if (Array.isArray(array)) {
            // const lastElement = array.at(-1); // Not supported by Safari
            const lastIdx = array.length - 1;
            const lastElement = array[lastIdx];
            if (field && util.isObject(lastElement)) {
                if (util.isDefined(lastElement[field])) {
                    return lastElement[field];
                }
                return fallback;
            }
            return lastElement || fallback;
        }
        return fallback;
    }

    static scrollTo(element, top = 0, left = 0) {
        // element.scrollTop = to;
        element.scrollTo({
            top,
            left,
            behavior: 'smooth'
        });
    }

    static tapticWeakBoom() {
        if (navigator.vibrate) {
            console.log('tapticWeakBoom');
            // navigator.vibrate(10);
        }
    }

    static tapticStrongBoom() {
        if (navigator.vibrate) {
            // navigator.vibrate(10);
        }
    }

    static tapticBurst() {
        if (navigator.vibrate) {
            // navigator.vibrate(10);
        }
    }

    static tapticNotification(type) {
        if (navigator.vibrate) {
            console.log('tapticNotification');
            // navigator.vibrate(50);
        }
    }

    static tapticSelection() {
        if (navigator.vibrate) {
            // navigator.vibrate(50);
        }
    }

    static tapticImpact(style) {
        if (navigator.vibrate) {
            // navigator.vibrate(50);
        }
    }

    static tapticGestureSelectionStart() {
        if (navigator.vibrate) {
            // navigator.vibrate(10);
        }
    }

    static tapticGestureSelectionChanged() {
        if (navigator.vibrate) {
            // navigator.vibrate(10);
        }
    }

    static tapticGestureSelectionEnd() {
        if (navigator.vibrate) {
            // navigator.vibrate(10);
        }
    }

    static getAlphabet(length = 26, concat = []) {
        const alpha = Array.from(Array(length)).map((e, i) => i + 65);
        const alphabet = alpha.map((x) => String.fromCharCode(x));
        return alphabet.concat(concat);
    }

    static getPillClass(length, idx) {
        let classNames;
        if (length === 1) {
            classNames = 'rounded-pill-both';
        } else if (idx === 0) {
            classNames = 'rounded-pill-left';
        } else if (idx + 1 === length) {
            classNames = 'rounded-pill-right';
        }
        return classNames;
    }

    static md5(str) {
        var chk = 0x12345678;
        var len = str.length;
        for (var i = 0; i < len; i++) {
            chk += (str.charCodeAt(i) * (i + 1));
        }

        return (chk & 0xffffffff).toString(16);
    }

    static imageKeywords(img) {
        if (typeof img === 'object') {
            const keywords = [];
            if (typeof img.predictions === 'object' && Array.isArray(img.predictions)) {
                for (let i = 0, l = img.predictions.length; i < l; i += 1) {
                    const el = img.predictions[i];
                    if (el.probability >= 0.1) {
                        keywords.push(el.className);
                    }
                }
            }
            if (typeof img.predictionsCocoSsd === 'object' && Array.isArray(img.predictionsCocoSsd)) {
                for (let i = 0, l = img.predictionsCocoSsd.length; i < l; i += 1) {
                    const el = img.predictionsCocoSsd[i];
                    if (el.score > 0.1) {
                        keywords.push(el.class);
                    }
                }
            }
            // Make unique array
            return keywords.filter(onlyUnique);
        }
        return null;
    }

    static median(arr) {
        const middle = Math.floor(arr.length / 2);
        const sortedArr = [...arr].sort((a, b) => a - b);
        return sortedArr.length % 2 !== 0 ? sortedArr[middle] : (sortedArr[middle - 1] + sortedArr[middle]) / 2;
    }

    static avgOfValues(arr) {
        const arrayOfValues = arr.filter(v => v > 0);
        const average = arrayOfValues.reduce((a, b) => a + b, 0) / arrayOfValues.length;
        return average;
    }

    // use local storage for messaging. Set message in local storage and clear it right away
    // This is a safe way how to communicate with other tabs while not leaving any traces
    //
    static messageBroadcast(message) {
        localStorage.setItem('message',JSON.stringify(message));
        localStorage.removeItem('message');
    }

    // receive message
    //
    static messageReceive(e) {
        if (e.key!='message') return; // ignore other keys
        try {
            const message = JSON.parse(e.newValue);
            if (!message) return; // ignore empty msg or msg reset
            return message;
        } catch (e) {
            console.log(e);
        }
    }

    static youtubeVideo(url) {
        if (!url) {
            return '';
        }
        let youtubeRegex;
        let isShort = false;

        // https://youtube.com/shorts/HcE_tI9KZWs?feature=share

        if (url.match(/youtube\.com\/shorts/)) {
            // eslint-disable-next-line
            youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtube\.com\/shorts\/(([^?&\s<$]+)((\?|&)[^\s<$]+)*)/gi;
            isShort = true;
        } else if (url.match(/youtube/)) {
            // eslint-disable-next-line
            youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtube\.com\/(.*?v=([^&\s<$]+)(&[^\s<$]+)*)/gi;
        } else if (url.match(/youtu\.be/)) {
            // eslint-disable-next-line
            youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtu\.be\/(([^?&\s<$]+)((\?|&)[^\s<$]+)*)/gi;
        } else {
            return null;
        }
        // const regexp = /(^|[\s\t\n]+)https*:\/\/(www\.)*youtube\.com\/(.*?v=([^&\s]+)(&[^\s]+)*)/gi;
        const youtubeVideo = url.replace(youtubeRegex, (match, p0, p1, p2, p3, p4) => {
            return p3;
        });
        // return youtubeVideo;
        if (isShort) {
            return (<>
                <div class={`w-50 float-right ps-3 embed-responsive embed-responsive-1by1`} >
                    <iframe class='embed-responsive-item' src={`https://www.youtube.com/embed/${youtubeVideo}?rel=0`} allowfullscreen></iframe>
                </div>
            </>);
        }
        return (<>
            <div class={`embed-responsive embed-responsive-16by9`} >
                <iframe class='embed-responsive-item' src={`https://www.youtube.com/embed/${youtubeVideo}?rel=0`} allowfullscreen></iframe>
            </div>
        </>);
    }

    static youtubeThumb($content, $size = 'medium') {
        if (typeof $content !== 'string') {
            return undefined;
        }
        let youtubeRegex;
        if ($content.match(/youtube\.com\/shorts/)) {
            // eslint-disable-next-line
            youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtube\.com\/shorts\/(([^?&\s<$]+)((\?|&)[^\s<$]+)*)/gi;
        } else if ($content.match(/youtube/)) {
            // eslint-disable-next-line
            youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtube\.com\/(.*?v=([^&\s<$]+)(&[^\s<$]+)*)/gi;
        } else if ($content.match(/youtu\.be/)) {
            // eslint-disable-next-line
            youtubeRegex = /(<p>|<br>|[\s\t\n]|^)*https*:\/\/(www\.)*youtu\.be\/(([^?&\s<$]+)((\?|&)[^\s<$]+)*)/gi;
        } else {
            return null;
        }
        const youtubeMatch = youtubeRegex.exec($content);
        const sizes = {
            xsmall: 'default',
            small: 'mqdefault',
            medium: 'hqdefault',
            large: 'sddefault',
            max: 'maxresdefault',
        };
        let youtubeVideoThumb;
        if (typeof youtubeMatch === 'object' && Array.isArray(youtubeMatch)) {
            youtubeVideoThumb = `//img.youtube.com/vi/${youtubeMatch[4]}/${sizes[$size]}.jpg`;
        }
        return youtubeVideoThumb;
    }

    /**
     * Maps a file extension to the corresponding Font Awesome file icon class.
     *
     * Removes any leading dot from the extension, converts it to lowercase,
     * and returns the matching Font Awesome class name. If the extension
     * is not recognized, returns the generic 'fa-file' class.
     *
     * @static
     * @param {string} extension - The file extension (with or without a leading dot).
     * @returns {string} The Font Awesome icon class name for the given extension.
     */
    static resolveFontawesomeFiletype(extension) {
        extension = extension.replace(/^\./, '');
        const mapping = {
            'jpg': 'fa-file-image',
            'png': 'fa-file-image',
            'gif': 'fa-file-image',
            'pdf': 'fa-file-pdf',
            'txt': 'fa-file-alt',
            'md': 'fa-file-alt',
            'doc': 'fa-file-word',
            'docx': 'fa-file-word',
            'xls': 'fa-file-excel',
            'xlsx': 'fa-file-excel',
            'ppt': 'fa-file-powerpoint',
            'pptx': 'fa-file-powerpoint',
            'zip': 'fa-file-archive',
            'rar': 'fa-file-archive',
            'js': 'fa-file-code',
            'html': 'fa-file-code',
            'css': 'fa-file-code',
            'mp3': 'fa-file-audio',
            'wav': 'fa-file-audio',
            'mp4': 'fa-file-video',
            'avi': 'fa-file-video',
        };
        return mapping[extension.toLowerCase()] || 'fa-file';
    }

    /**
     * Converts a file size in bytes to a human-readable string with appropriate units (B, KB, MB, GB).
     *
     * @param {number} bytes - The file size in bytes.
     * @returns {string} The formatted file size string.
     */
    static formatFileSize(bytes) {
        if (bytes < 1024) {
            return bytes + ' B';
        } else if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(0) + ' KB';
        } else if (bytes < 1024 * 1024 * 1024) {
            return (bytes / (1024 * 1024)).toFixed(0) + ' MB';
        } else {
            return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        }
    }

    /**
     * Returns the image source URL for a given image object and size.
     *
     * @param {Object} img - The image object containing S3 links for various sizes.
     * @param {Object} props - Additional properties (currently unused).
     * @param {string} [size='150x'] - The desired image size. Supported values: '150x', '220x', '400x', '800x', '1024x', '1280x', '1600x', '1920x'.
     * @returns {string|undefined} The URL of the image for the specified size, or undefined if the input is invalid.
     */
    static displayImg(img, props, size = '150x') {
        if (typeof img !== 'object') {
            return undefined;
        }
        let imgSrc = `${img.s3MediumLink}`;
        switch (size) {
            case '150x':
                imgSrc = `${img.s3XSmallLink}`;
                break;
            case '220x':
                imgSrc = `${img.s3SmallLink}`;
                break;
            case '400x':
                imgSrc = `${img.s3MediumLink}`;
                break;
            case '800x':
                imgSrc = `${img.s3LargeLink}`;
                break;
            case '1024x':
                imgSrc = `${img.s3XLargeLink}`;
                break;
            case '1280x':
                imgSrc = `${img.s3XXLargeLink}`;
                break;
            case '1600x':
                imgSrc = `${img.s33XLargeLink}`;
                break;
            case '1920x':
                imgSrc = `${img.s34XLargeLink}`;
                break;
            default:
                imgSrc = `${img.s3MediumLink}`;
        }

        return imgSrc;
    }

    /**
     * Converts a hex color string to its HSL (Hue, Saturation, Lightness) representation.
     *
     * @param {string} hexColor - The hex color string (e.g., "#ff0000" or "ff0000").
     * @returns {string} The HSL color string in the format "hsl(h, s%, l%)".
     */
    static hexToHSL(hexColor) {
        // Remove the hash at the start if it's there
        hexColor = hexColor.replace(/^#/, '');

        // Parse the r, g, b values
        let r = parseInt(hexColor.substring(0, 2), 16);
        let g = parseInt(hexColor.substring(2, 4), 16);
        let b = parseInt(hexColor.substring(4, 6), 16);

        // Convert r, g, b to percentages
        r /= 255;
        g /= 255;
        b /= 255;

        // Calculate l, s, and h
        const cmin = Math.min(r, g, b),
              cmax = Math.max(r, g, b),
              delta = cmax - cmin;
        let h = 0, s = 0, l = 0;

        if (delta === 0) h = 0;
        else if (cmax === r) h = ((g - b) / delta) % 6;
        else if (cmax === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;

        h = Math.round(h * 60);
        if (h < 0) h += 360;

        l = (cmax + cmin) / 2;
        s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
        s = +(s * 100).toFixed(1);
        l = +(l * 100).toFixed(1);

        return `hsl(${h}, ${s}%, ${l}%)`;
    }

    /**
     * Computes the complementary color for a given hex code.
     *
     * @param {string} hex - The source color in hexadecimal format (e.g. "#RRGGBB" or "RRGGBB").
     * @returns {string} The complementary color as an HSL string (e.g. "hsl(180, 50%, 50%)").
     */
    static getComplimentaryColor(hex) {
        const hsl = LocalUtil.hexToHSL(hex);
        // let [h, s, l] = hsl.match(/\d+/g);
        let [h, s, l] = hsl.match(/[\d.]+/g);

        h = (parseInt(h) + 180) % 360;
        return `hsl(${h}, ${s}%, ${l}%)`;
    }

    /**
     * Shifts the hue, saturation, and lightness of a hex color.
     *
     * @param {string} hex - Hex color string (e.g. '#RRGGBB' or 'RRGGBB').
     * @param {number} [hueShift=10] - Degrees to shift the hue (0–360).
     * @param {number} [saturationShift=5] - Percentage points to shift saturation (0–100).
     * @param {number} [lightnessShift=5] - Percentage points to shift lightness (0–100).
     * @returns {string} The resulting color in HSL format: 'hsl(h, s%, l%)'.
     */
    static shiftColor(hex, hueShift = 10, saturationShift = 5, lightnessShift = 5) {
        // Check if validity of hex color
        if (!/^#?[0-9A-F]{6}$/i.test(hex)) {
            return hex;
        }
        if (!hex) {
            return hex;
        }
        const hsl = LocalUtil.hexToHSL(hex);
        // let [h, s, l] = hsl.match(/\d+/g);
        let [h, s, l] = hsl.match(/[\d.]+/g);

        h = (parseInt(h) + hueShift) % 360; // Adjust hue
        s = Math.min(100, parseInt(s) + saturationShift); // Adjust saturation
        l = Math.min(100, parseInt(l) + lightnessShift); // Adjust lightness
        return `hsl(${h}, ${s}%, ${l}%)`;
    }

    // static findCountryByName(name) {
    //     return 'no';
    // }

    /**
     * Finds the ISO2 code for a country by matching a partial or full name.
     *
     * This method first attempts to find countries in the `COUNTRIES` list whose
     * `country` property contains the provided `inputName` (case-insensitive).
     * If no matches are found, it then looks for matches in the `EUROPEAN_COUNTRIES`
     * list by `localName` and uses those to filter the original `COUNTRIES` list.
     *
     * @param {string} inputName - The (partial) country name to search for.
     * @returns {string|null} The ISO 3166-1 alpha-2 code of the first matching country,
     *                        or `null` if `inputName` is falsy or no match is found.
     */
    static findCountryByName(inputName) {
        if (!inputName) {
            return null;
        }
        const partialName = `${inputName}`.toLowerCase().trim();
        // Filter countries that include the partialName in their country name
        let matchedCountries = COUNTRIES.filter(country =>
            country.country?.toLowerCase().includes(partialName.toLowerCase())
        );
        if (matchedCountries.length === 0) {
            const matchedEuropeeanCountries = EUROPEAN_COUNTRIES.filter(country =>
                country.localName?.toLowerCase().includes(partialName.toLowerCase())
            );
            matchedCountries = COUNTRIES.filter(country =>
                matchedEuropeeanCountries.find(e => e.country === country.country)
            );
        }
        if (matchedCountries.length === 0) {
            return null;
        }
        if (matchedCountries.length >= 1) {
            return matchedCountries[0].iso2;
        }
        return matchedCountries;
    }

    /**
     * Returns a country flag SVG or placeholder span, either as a React element or raw HTML string.
     *
     * @param {string} country - The full name of the country whose flag to retrieve.
     * @param {number} [height=24] - The height of the flag in pixels.
     * @param {boolean} [isSquare=false] - Whether to force the flag to be square (width = height).
     * @param {string} [className=''] - Additional CSS class(es) to apply to the SVG element.
     * @param {string} [style=''] - Inline CSS styles to apply to the SVG element.
     * @param {boolean} [asString=false] - If true, returns the SVG/placeholder as an HTML string; otherwise returns a JSX element.
     * @returns {JSX.Element|string} The flag SVG (or placeholder) as a React node or string.
     */
    static getFlagSvgByCountryCode(countryCode, height = 24, isSquare = false, className = '', style = '', asString) {
        let notFound = <div style={`display: inline-block; width: ${isSquare ? height : (height / 0.75)}px; height: ${height}px; ${style}`} class={className} />;
        if (asString) {
            notFound = `<span style="display: inline-block; width: ${isSquare ? height : (height / 0.75)}px; height: ${height}px;"></span>`;
        }
        if (!countryCode || countryCode === 'null') {
            return notFound;
        }
        // console.log({ countryCode })
        if (asString) {
            return `<img
                class="${className}"
                src="/assets/flags/${isSquare ? '1x1' : '4x3'}/${`${countryCode}`.toLowerCase()}.svg"
                style="width: auto; height: ${height}px; ${style}"
            />`;
        }
        return <img
            class={className}
            src={`/assets/flags/${isSquare ? '1x1' : '4x3'}/${`${countryCode}`.toLowerCase()}.svg`}
            style={`width: auto; height: ${height}px; ${style}`}
        />;
    }

    /**
     * Returns the SVG string representation of a country's flag.
     *
     * @param {string} country - The country code or name for which to get the flag SVG.
     * @param {number} [height=24] - The height of the SVG in pixels.
     * @param {boolean} [isSquare=false] - Whether the flag should be rendered as a square.
     * @param {string} [className=''] - Optional CSS class name(s) to apply to the SVG element.
     * @param {string} [style=''] - Optional inline CSS styles to apply to the SVG element.
     * @returns {string} The SVG string representing the country's flag.
     */
    static getFlagSvgString(country, height = 24, isSquare = false, className = '', style = '') {
        // const countryCode = LocalUtil.resolveCountry(country);
        const countryCode = LocalUtil.resolveIsoCode(country);
        return LocalUtil.getFlagSvgByCountryCode(countryCode, height, isSquare, className, style, true);
    }

    /**
     * Returns the SVG html element representation of a country's flag.
     *
     * @param {string} country - The country code or name for which to get the flag SVG.
     * @param {number} [height=24] - The height of the SVG in pixels.
     * @param {boolean} [isSquare=false] - Whether the flag should be rendered as a square.
     * @param {string} [className=''] - Optional CSS class name(s) to apply to the SVG element.
     * @param {string} [style=''] - Optional inline CSS styles to apply to the SVG element.
     * @returns {string} The SVG string representing the country's flag.
     */
    static getFlagSvg(country, height = 24, isSquare = false, className = '', style = '') {
        // const countryCode = LocalUtil.resolveCountry(country);
        const countryCode = LocalUtil.resolveIsoCode(country);
        return LocalUtil.getFlagSvgByCountryCode(countryCode, height, isSquare, className, style, false);
    }

   // small LRU to memoize hot lookups
    static _cache = new Map(); // input -> code
    static _cacheCap = 512;
    static _getCached(key) { return LocalUtil._cache.get(key); }
    static _setCached(key, val) {
        LocalUtil._cache.set(key, val);
        if (LocalUtil._cache.size > LocalUtil._cacheCap) {
            const [oldestKey] = LocalUtil._cache.keys();
            LocalUtil._cache.delete(oldestKey);
        }
    }

    static normalizeName(s) {
        return String(s || '')
            .toLowerCase()
            .normalize('NFD').replace(reMarks, '')
            .replace(reSpace, ' ')
            .trim();
    }

    // Levenshtein with band (maxDist) and 2 rows
    static levenshteinBounded(a, b, maxDist) {
        const n = a.length, m = b.length;
        if (a === b) return 0;
        if (Math.abs(n - m) > maxDist) return maxDist + 1; // length gap too big

        // ensure a is shorter
        if (n > m) { const t = a; a = b; b = t; }

        const prev = new Uint16Array(m + 1);
        const curr = new Uint16Array(m + 1);
        for (let j = 0; j <= m; j++) prev[j] = j;

        for (let i = 1; i <= n; i++) {
            curr[0] = i;
            // band limits
            const from = Math.max(1, i - maxDist);
            const to = Math.min(m, i + maxDist);
            // left of band: force > maxDist to avoid picking it
            for (let j = 1; j < from; j++) curr[j] = maxDist + 1;

            for (let j = from; j <= to; j++) {
                const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
                const del = prev[j] + 1;
                const ins = curr[j - 1] + 1;
                const sub = prev[j - 1] + cost;
                let v = del < ins ? del : ins;
                if (sub < v) v = sub;
                curr[j] = v;
            }
            // right of band
            for (let j = to + 1; j <= m; j++) curr[j] = maxDist + 1;

            // early exit if best in this row already exceeds maxDist
            let rowMin = maxDist + 1;
            for (let j = from; j <= to; j++) if (curr[j] < rowMin) rowMin = curr[j];
            if (rowMin > maxDist) return maxDist + 1;

            // swap rows
            for (let j = 0; j <= m; j++) prev[j] = curr[j];
        }
        return prev[m];
    }

    static bestAliasMatch(name) {
        const aliases = Object.keys(ukSubdivisions);
        let best = { key: null, dist: Infinity };
        for (const k of aliases) {
            const d = LocalUtil.levenshteinBounded(name, k, 2);
            if (d < best.dist) best = { key: k, dist: d };
        }
        return best.dist <= 2 ? ukSubdivisions[best.key] : null;
    }

    static tieBreak(q, cand, curr) {
        const prefix = (a, b) => {
            let i = 0;
            while (i < a.length && i < b.length && a[i] === b[i]) i++;
            return i;
        };
        const cp = prefix(q, cand || '');
        const bp = prefix(q, curr || '');
        if (cp !== bp) return cp > bp;
        const cd = Math.abs((cand || '').length - q.length);
        const bd = Math.abs((curr || '').length - q.length);
        return cd < bd;
    }

    static getCountryAlpha2Fuzzy(input) {
        const cached = LocalUtil._getCached(`country:${input}`);
        if (cached) return cached;

        const plain = String(input || '').trim();
        const target = LocalUtil.normalizeName(plain);

        // fast paths: exact alpha-2 or GB-XXX
        if (/^[A-Za-z]{2}$/.test(plain)) return plain.toUpperCase();
        if (/^[A-Za-z]{2}-[A-Za-z]{3}$/.test(plain)) return plain.toUpperCase();

        // direct lookups across locales
        for (const lang of langs) {
            const hit = countries.getAlpha2Code(plain, lang);
            if (hit) { LocalUtil._setCached(`country:${input}`, hit); return hit; }
        }

        // alias hits
        if (aliasToAlpha2[target]) {
            LocalUtil._setCached(`country:${input}`, aliasToAlpha2[target]);
            return aliasToAlpha2[target];
        }

        // exact name hit
        const exact = nameToAlpha2.get(target);
        if (exact) { LocalUtil._setCached(`country:${input}`, exact); return exact; }

        // candidate pruning via 3-char prefix
        let candidates = null;
        if (target.length >= 3) {
            const p = target.slice(0, 3);
            const set = prefixIndex.get(p);
            if (set) candidates = Array.from(set);
        }
        if (!candidates) candidates = Array.from(alpha2ToNames.keys());

        // fuzzy among candidates with bounded distance (dynamic threshold)
        let best = { alpha2: null, name: null, dist: Infinity };
        for (const alpha2 of candidates) {
            const names = alpha2ToNames.get(alpha2);
            let minDist = Infinity;
            let minName = null;

            // dynamic max distance: stricter for short queries
            const maxDist = Math.max(1, Math.floor(Math.min(target.length, 10) * 0.3));

            for (const n of names) {
                const d = LocalUtil.levenshteinBounded(target, n, maxDist);
                if (d < minDist) {
                    minDist = d;
                    minName = n;
                    if (d === 0) break;
                }
            }

            if (
                minDist < best.dist ||
                (minDist === best.dist && LocalUtil.tieBreak(target, minName, best.name))
            ) {
                best = { alpha2, name: minName, dist: minDist };
            }
        }

        const ok = best.alpha2 && best.dist !== Infinity && best.dist <= 2;
        const out = ok ? best.alpha2 : null;
        if (out) LocalUtil._setCached(`country:${input}`, out);
        return out;
    }

    static resolveIsoCode(name = '') {
        const cached = LocalUtil._getCached(`iso:${name}`);
        if (cached) return cached;

        const norm = LocalUtil.normalizeName(name);

        // UK subdivisions (exact or fuzzy)
        const ukSub = ukSubdivisions[norm] || LocalUtil.bestAliasMatch(norm);
        if (ukSub) { LocalUtil._setCached(`iso:${name}`, ukSub); return ukSub; }

        // Countries (3166-1 alpha-2)
        const alpha2 = LocalUtil.getCountryAlpha2Fuzzy(name);
        if (alpha2) { LocalUtil._setCached(`iso:${name}`, alpha2); return alpha2; }

        return null;
    }

    /**
     * Resolves a country name or ISO code to its ISO 3166-1 alpha-2 country code.
     *
     * Attempts to match the input country name in English and Norwegian, or validates
     * if the input is already a valid ISO code. Returns the ISO code if found, or null otherwise.
     *
     * @param {string} countryName - The name or ISO code of the country to resolve.
     * @returns {string|null} The resolved ISO 3166-1 alpha-2 country code, or null if not found.
     */
    static resolveCountry(countryName = '') {
        // Normalize the input
        const name = countryName.trim().toLowerCase();
        // Try to get the country code from English names
        let countryCode = countries.getAlpha2Code(name, 'en');
        if (!countryCode) {
            // Try to get the country code from Norwegian names
            countryCode = countries.getAlpha2Code(name, 'no');
        }
        if (!countryCode) {
            // Try if the name is already an ISO code
            countryCode = name.toUpperCase();
            if (!countries.isValid(countryCode)) {
                return null; // Could not resolve the country
            }
        }
        return countryCode;
    }

    /**
     * Returns a comparator function for sorting objects by multiple fields.
     * Each field can be prefixed with '-' to indicate descending order.
     * Handles `null` and `undefined` values by placing them after defined values.
     *
     * @param {string[]} fields - Array of field names to sort by. Prefix with '-' for descending order.
     * @returns {(a: Object, b: Object) => number} Comparator function for use with Array.prototype.sort.
     */
    static fieldSorter(fields) {
        return (a, b) => {
            for (let field of fields) {
                let dir = 1;
                if (field[0] === '-') {
                    dir = -1;
                    field = field.substring(1);
                }

              const aValue = a[field];
              const bValue = b[field];

              // Handle undefined and null values
              const aIsNull = aValue === null || aValue === undefined;
              const bIsNull = bValue === null || bValue === undefined;

              if (aIsNull && bIsNull) {
                  // Both values are null or undefined; consider them equal and continue to the next field
                  continue;
              } else if (aIsNull) {
                  // Only aValue is null or undefined; place it after bValue
                  return -dir;
              } else if (bIsNull) {
                  // Only bValue is null or undefined; place aValue before bValue
                  return dir;
              } else if (aValue > bValue) {
                  return dir;
              } else if (aValue < bValue) {
                  return -dir;
              }
              // If values are equal, continue to the next field
          }
          return 0; // All fields are equal
        };
    }

    static getUsageReason(reason) {
        switch (reason) {
            case '1':
                return 'Other';
            case '2':
                return 'Forebyggende';
            case '3':
                return 'Sykdom';
            default:
                return '';
        }
    }

    static getThreatmentBy(by) {
        switch (by) {
            case '1':
                return 'Dyreeier/andre';
            case '2':
                return 'Veterinær';
            default:
                return '';
        }
    }


    /*
        static dateDiff(start, end) {
            if (!start) {
                return {};
            }
            const fromDate = Utilities.parseInputDate(start);
            const endDate = Utilities.parseInputDate(end);
            const deltaMs = endDate.getTime() - fromDate.getTime();
            const deltaSec = Math.floor(deltaMs / 1000);
            const deltaDays = Math.floor(deltaSec / 86400);

            return {
                seconds: deltaSec,
                minutes: Math.floor(deltaSec / 60),
                hours: Math.floor(deltaSec / 3600),
                days: deltaDays,
                weeks: Math.floor(deltaDays / 7),
                months: Math.floor(deltaDays / 30),
                years: Math.floor(deltaDays / 365),
            };
        }
    */

    /**
     * Returns a human-readable string representing the time elapsed since the given date.
     *
     * The output is formatted as:
     * - "Now" for less than or equal to 1 second ago
     * - "{n}s" for seconds
     * - "{n}m" for minutes
     * - "{n}h" for hours
     * - "{n}d" for days
     * - "1w" or "{n}w" for weeks
     * - Formatted date string for more than 4 weeks ago
     *
     * @param {Date|string|number} dateInput - The date to compare against the current time. Can be a Date object, ISO string, or timestamp.
     * @returns {string} Human-readable time ago string.
     */
    static getTimeAgo(dateInput) {
        const now = new Date();
        const { seconds, minutes, hours, days, weeks } = util.dateDiff(dateInput, now);
        if (weeks > 4) {
            return util.formatDate(dateInput, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' });
        }
        if (weeks > 1) {
            return `${weeks}w`;
        }
        if (weeks === 1) {
            return '1w';
        }
        if (days > 1) {
            return `${days}d`;
        }
        if (days === 1) {
            return '1d';
        }
        if (hours > 1) {
            return `${hours}h`;
        }
        if (hours === 1) {
            return '1h';
        }
        if (minutes > 1) {
            return `${minutes}m`;
        }
        if (minutes === 1) {
            return '1m';
        }
        if (seconds > 1) {
            return `${seconds}s`;
        }
        return 'Now';
    }

    static makeShortFileName(fileName, maxLength = 10) {
        if (typeof fileName !== 'string') {
            return '';
        }
        if (fileName.length <= maxLength) {
            return fileName;
        }

        // split into name and extension
        const lastDot = fileName.lastIndexOf('.');
        let name = fileName;
        let ext  = '';
        if (lastDot > 0) {
            name = fileName.slice(0, lastDot);
            ext  = fileName.slice(lastDot);
        }

        const minChars       = 2;                // at least 2 on each side
        const allowedNameLen = maxLength;        // budget for name + ellipsis
        const minAllowed     = minChars * 2 + 3; // 3 prefix + 3 suffix + 3 dots

        // if we can’t afford 3 + 3 + 3, or name is already short, bail out
        if (allowedNameLen < minAllowed || name.length <= minChars * 2 + 3) {
            return fileName;
        }

        // distribute the remaining chars between prefix/suffix
        const keep        = allowedNameLen - 3;                  // minus ellipsis
        const prefixLen   = Math.ceil(keep / 2);
        const suffixLen   = Math.floor(keep / 2);

        const prefix = name.slice(0, prefixLen);
        const suffix = name.slice(name.length - suffixLen);

        return `${prefix}...${suffix}${ext}`;
    }

    static toggleBootstrap53DarkMode(isEnabled, hue) {
        const htmlElement = document.documentElement;
        if (isEnabled) {
            htmlElement.setAttribute('data-bs-theme', 'dark');
            document.body.setAttribute('data-bs-theme', 'dark');
            document.body.classList.remove('bootstrap');
            document.body.classList.add('bootstrap-dark');
            if (window.StatusBar) {
                window.StatusBar.backgroundColorByName('darkGray');
                window.StatusBar.styleLightContent();
            }
        } else {
            htmlElement.setAttribute('data-bs-theme', 'light');
            document.body.setAttribute('data-bs-theme', 'light');
            document.body.classList.remove('bootstrap-dark');
            document.body.classList.add('bootstrap');
            if (window.StatusBar) {
                window.StatusBar.backgroundColorByName('lightGray');
                window.StatusBar.styleDefault();
            }
        }

        if (hue) {
            htmlElement.setAttribute('data-hue', hue);
            document.body.setAttribute('data-hue', hue);
        } else {
            htmlElement.removeAttribute('data-hue');
            document.body.removeAttribute('data-hue');
        }
    }

    static isDark(hex) {
        const n = hex.replace('#', '');
        const r = parseInt(n.slice(0, 2), 16);
        const g = parseInt(n.slice(2, 4), 16);
        const b = parseInt(n.slice(4, 6), 16);
        const l = (0.2126 * r + 0.7152 * g + 0.0722 * b); // 0..255
        return l < 140;
    }

    /**
     * Creates a safe copy of a product for MD5 calculation by excluding
     * potentially circular fields (subProducts, originalSubProduct).
     * Use this instead of spreading the full product in JSON.stringify.
     * @param {Object} product - The product object
     * @returns {Object} Product without circular reference fields
     */
    static safeProductForMd5(product) {
        const { subProducts, originalSubProduct, ...safeProduct } = product;
        return safeProduct;
    }

    /**
     * Extracts subProducts up to 2 levels deep from a product.
     * Handles circular references gracefully (e.g., Product A -> B -> A).
     * @param {Object} product - The main product object
     * @param {Array} allProducts - Array of all products from productStore (not used if products already enriched)
     * @param {Object} options - Options for product line creation
     * @param {Object} options.user - The current user object
     * @param {Object|null} options.animal - Optional animal object { id, name }
     * @returns {Array} Array of subProduct objects ready to add to sale
     */
    static extractSubProductsWithLevel2(product, allProducts, options) {
        const { user, animal = null } = options;
        const subProducts = [];
        const processedIds = new Set(); // Prevent duplicates and cycles

        // Add the main product ID to prevent self-reference cycles
        if (product.id) {
            processedIds.add(product.id);
        }

        const createEntry = (p) => {
            // Use safeProductForMd5 to exclude circular reference fields from JSON.stringify
            const safeP = LocalUtil.safeProductForMd5(p);
            const newProduct = {
                qty: 1,
                ...p,
                createdDate: new Date(),
                md5: md5(JSON.stringify({ ...safeP, createdDate: new Date() })).toString(),
                saleUser: user.id,
                saleDepartment: user.currentCustomerDepartment,
            };
            if (animal) {
                newProduct.animal = animal;
            }
            newProduct.qty = parseFloat(newProduct.qty) || 0;
            return newProduct;
        };

        // Process level-1 subProducts (already enriched from backend)
        (product?.subProducts || []).forEach(subProduct => {
            // Use id, md5, or externalId as identifier for cycle detection
            // Some subProducts may not have id if not found in database
            const subProductKey = subProduct.id || subProduct.md5 || subProduct.externalId;
            if (subProductKey && processedIds.has(subProductKey)) return; // Skip cycles
            if (subProductKey) processedIds.add(subProductKey);
            subProducts.push(createEntry(subProduct));

            // Level-2: Use subProducts from the already-enriched level-1 product
            // Backend now includes enriched level-2 subProducts directly in level-1 products
            (subProduct?.subProducts || []).forEach(level2 => {
                const level2Key = level2.id || level2.md5 || level2.externalId;
                if (level2Key && processedIds.has(level2Key)) return; // Skip cycles
                if (level2Key) processedIds.add(level2Key);
                subProducts.push(createEntry(level2));
            });
        });

        return subProducts;
    }

}

export default LocalUtil;
